import React, { useState, useRef, useEffect, useCallback } from "react";

const API_BASE =
  process.env.REACT_APP_API_URL || "http://localhost:3002";

function formatBytes(bytes) {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}
function formatDate(d) { return new Date(d).toLocaleString(); }
function formatINR(v) { return "₹" + parseFloat(v).toLocaleString("en-IN", { minimumFractionDigits: 2 }); }

function AgentCard({ icon, title, desc, selected, onClick }) {
  return (
    <div onClick={onClick} style={{
      flex: "1 1 160px",
      background: selected ? "rgba(99,102,241,0.18)" : "rgba(255,255,255,0.04)",
      border: selected ? "1.5px solid #6366f1" : "1.5px solid rgba(255,255,255,0.08)",
      borderRadius: 12, padding: "18px 14px", cursor: "pointer", transition: "all 0.2s", textAlign: "center",
    }}>
      <div style={{ fontSize: 28, marginBottom: 6 }}>{icon}</div>
      <div style={{ fontWeight: 600, fontSize: 12, letterSpacing: "0.08em", color: selected ? "#a5b4fc" : "#e2e8f0" }}>{title}</div>
      <div style={{ fontSize: 11, color: "#64748b", marginTop: 3 }}>{desc}</div>
    </div>
  );
}

// ── Mini Bar Chart ─────────────────────────────────────────────
const CHART_COLORS = ["#6366f1", "#8b5cf6", "#ec4899", "#f59e0b", "#10b981", "#3b82f6"];

// ── Grouped Bar Chart ──────────────────────────────────────────
function BarChart({ data, label }) {
  if (!data || data.length === 0)
    return <div style={{ color: "#475569", fontSize: 12, padding: "40px 0", textAlign: "center" }}>No usage data yet</div>;

  const W = 480, H = 180, PL = 32, PR = 12, PT = 16, PB = 36;
  const chartW = W - PL - PR, chartH = H - PT - PB;
  const maxVal = Math.max(...data.map(d => d.value), 1);
  const yTicks = 4;
  const barW = Math.min(48, (chartW / data.length) * 0.55);
  const gap = chartW / data.length;
  const [hovered, setHovered] = React.useState(null);

  return (
    <div style={{ width: "100%", overflowX: "auto" }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", maxWidth: W, display: "block" }}>
        {/* Y grid lines + labels */}
        {Array.from({ length: yTicks + 1 }, (_, i) => {
          const val = Math.round((maxVal * (yTicks - i)) / yTicks);
          const y = PT + (chartH * i) / yTicks;
          return (
            <g key={i}>
              <line x1={PL} x2={W - PR} y1={y} y2={y}
                stroke="rgba(255,255,255,0.06)" strokeWidth="1" strokeDasharray={i === yTicks ? "0" : "3,3"} />
              <text x={PL - 4} y={y + 4} textAnchor="end" fill="#475569" fontSize="9">{val}</text>
            </g>
          );
        })}

        {/* Bars */}
        {data.map((d, i) => {
          const barH = Math.max(2, (d.value / maxVal) * chartH);
          const x = PL + gap * i + gap / 2 - barW / 2;
          const y = PT + chartH - barH;
          const color = CHART_COLORS[i % CHART_COLORS.length];
          const isHov = hovered === i;
          return (
            <g key={i} onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)}
              style={{ cursor: "pointer" }}>
              {/* Bar shadow */}
              <rect x={x + 2} y={y + 2} width={barW} height={barH} rx="3"
                fill={color} opacity="0.15" />
              {/* Bar */}
              <rect x={x} y={y} width={barW} height={barH} rx="3"
                fill={isHov ? color : color} opacity={isHov ? 1 : 0.78}
                style={{ transition: "opacity 0.15s" }} />
              {/* Value label on top */}
              <text x={x + barW / 2} y={y - 4} textAnchor="middle" fill={color} fontSize="10" fontWeight="700">
                {d.value}
              </text>
              {/* X label */}
              <text x={x + barW / 2} y={H - 6} textAnchor="middle" fill={isHov ? "#e2e8f0" : "#64748b"} fontSize="9"
                style={{ transition: "fill 0.15s" }}>
                {d.label.length > 8 ? d.label.slice(0, 8) + "…" : d.label}
              </text>
              {/* Tooltip */}
              {isHov && (
                <g>
                  <rect x={x + barW / 2 - 34} y={y - 28} width={68} height={20} rx="4"
                    fill="#1e2130" stroke={color} strokeWidth="1" />
                  <text x={x + barW / 2} y={y - 14} textAnchor="middle" fill="#e2e8f0" fontSize="10" fontWeight="600">
                    {d.label}: {d.value}
                  </text>
                </g>
              )}
            </g>
          );
        })}

        {/* X axis baseline */}
        <line x1={PL} x2={W - PR} y1={PT + chartH} y2={PT + chartH}
          stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
      </svg>
      {label && <div style={{ fontSize: 11, color: "#64748b", textAlign: "center", marginTop: 4 }}>{label}</div>}
    </div>
  );
}

// ── Line Chart ─────────────────────────────────────────────────
function LineChart({ data, label }) {
  if (!data || data.length === 0)
    return <div style={{ color: "#475569", fontSize: 12, padding: "40px 0", textAlign: "center" }}>No daily data yet</div>;

  const W = 480, H = 160, PL = 32, PR = 12, PT = 16, PB = 32;
  const chartW = W - PL - PR, chartH = H - PT - PB;
  const maxVal = Math.max(...data.map(d => d.value), 1);
  const yTicks = 4;
  const [hovered, setHovered] = React.useState(null);

  const pts = data.map((d, i) => ({
    x: PL + (chartW * i) / Math.max(data.length - 1, 1),
    y: PT + chartH - (d.value / maxVal) * chartH,
    ...d,
  }));

  const polyline = pts.map(p => `${p.x},${p.y}`).join(" ");
  // Area fill path
  const areaPath = `M${pts[0].x},${PT + chartH} ` +
    pts.map(p => `L${p.x},${p.y}`).join(" ") +
    ` L${pts[pts.length - 1].x},${PT + chartH} Z`;

  return (
    <div style={{ width: "100%", overflowX: "auto" }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", maxWidth: W, display: "block" }}>
        <defs>
          <linearGradient id="lineArea" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#6366f1" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#6366f1" stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {/* Y grid */}
        {Array.from({ length: yTicks + 1 }, (_, i) => {
          const val = Math.round((maxVal * (yTicks - i)) / yTicks);
          const y = PT + (chartH * i) / yTicks;
          return (
            <g key={i}>
              <line x1={PL} x2={W - PR} y1={y} y2={y}
                stroke="rgba(255,255,255,0.06)" strokeWidth="1" strokeDasharray={i === yTicks ? "0" : "3,3"} />
              <text x={PL - 4} y={y + 4} textAnchor="end" fill="#475569" fontSize="9">{val}</text>
            </g>
          );
        })}

        {/* Area fill */}
        <path d={areaPath} fill="url(#lineArea)" />

        {/* Line */}
        <polyline points={polyline} fill="none" stroke="#6366f1" strokeWidth="2.5"
          strokeLinejoin="round" strokeLinecap="round" />

        {/* Dots + tooltip */}
        {pts.map((p, i) => (
          <g key={i} onMouseEnter={() => setHovered(i)} onMouseLeave={() => setHovered(null)}
            style={{ cursor: "pointer" }}>
            <circle cx={p.x} cy={p.y} r={hovered === i ? 6 : 4}
              fill={hovered === i ? "#6366f1" : "#0f1117"} stroke="#6366f1"
              strokeWidth={hovered === i ? 2.5 : 2}
              style={{ transition: "r 0.15s" }} />
            {/* X label */}
            {(i % Math.ceil(data.length / 8) === 0 || i === data.length - 1) && (
              <text x={p.x} y={H - 4} textAnchor="middle" fill="#475569" fontSize="9">{p.label}</text>
            )}
            {/* Tooltip */}
            {hovered === i && (
              <g>
                <rect x={p.x - 30} y={p.y - 30} width={60} height={20} rx="4"
                  fill="#1e2130" stroke="#6366f1" strokeWidth="1" />
                <text x={p.x} y={p.y - 16} textAnchor="middle" fill="#e2e8f0" fontSize="10" fontWeight="600">
                  {p.label}: {p.value}
                </text>
              </g>
            )}
          </g>
        ))}

        {/* Baseline */}
        <line x1={PL} x2={W - PR} y1={PT + chartH} y2={PT + chartH}
          stroke="rgba(255,255,255,0.12)" strokeWidth="1" />
      </svg>
      {label && <div style={{ fontSize: 11, color: "#64748b", textAlign: "center", marginTop: 4 }}>{label}</div>}
    </div>
  );
}

export default function AdminDashboard({ onLogout }) {
  const [activeSection, setActiveSection] = useState("ai-agents");

  // AI Agents state
  const [selectedArchetype, setSelectedArchetype] = useState(null);
  const [agentName, setAgentName] = useState("");
  const [customArchetypeTitle, setCustomArchetypeTitle] = useState("");
  const [customArchetypeDesc, setCustomArchetypeDesc] = useState("");
  const [customArchetypeIcon, setCustomArchetypeIcon] = useState("🤖");
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [libraryFiles, setLibraryFiles] = useState([]);
  const [deployedAgents, setDeployedAgents] = useState([]);
  const [pendingPdfFiles, setPendingPdfFiles] = useState([]);
  const [selectedAgentKey, setSelectedAgentKey] = useState(900001);
  const [agentTab, setAgentTab] = useState("departments");
  const [cacheAgentKey, setCacheAgentKey] = useState(900001);
  const [cacheSearch, setCacheSearch] = useState("");
  const [ragMisses, setRagMisses] = useState([]);
  const [xaiCache, setXaiCache] = useState([]);
  const [viewFileModal, setViewFileModal] = useState(null); // { filename, content, type }
  const [users, setUsers] = useState([]);
  const [activity, setActivity] = useState([]);
  const fileInputRef = useRef();

  // Coupon state
  const [coupons, setCoupons] = useState([]);
  const [couponForm, setCouponForm] = useState({ code: "", discount_pct: "", max_uses: 30, applicable_plan: "", expires_at: "" });
  const [couponStatus, setCouponStatus] = useState(null);
  const [couponStats, setCouponStats] = useState({ daily: [], totals: [] });
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [couponUsages, setCouponUsages] = useState([]);
  const [showUsagesFor, setShowUsagesFor] = useState(null);

  // Plans state
  const [plans, setPlans] = useState([]);
  const [planForm, setPlanForm] = useState({ name: "", chat_limit: "", price: "", description: "" });
  const [planStatus, setPlanStatus] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);

  // Emotions state
  const [emotions, setEmotions] = useState([]);
  const [emotionSearch, setEmotionSearch] = useState("");
  const [emotionUploading, setEmotionUploading] = useState(false);
  const [emotionStatus, setEmotionStatus] = useState(null);
  const [emotionDragOver, setEmotionDragOver] = useState(false);
  const [generatingKnowledge, setGeneratingKnowledge] = useState(false);
  const [knowledgeGenStatus, setKnowledgeGenStatus] = useState(null);

  // Developer API Keys state
  const [apiKeys, setApiKeys] = useState([]);
  const [apiKeyName, setApiKeyName] = useState("");
  const [apiKeyStatus, setApiKeyStatus] = useState(null);
  const [revealedKeys, setRevealedKeys] = useState({});
  const [copiedKeyId, setCopiedKeyId] = useState(null);
  const [isRefreshingApiKeys, setIsRefreshingApiKeys] = useState(false);
  const [selectedHookupKey, setSelectedHookupKey] = useState("");
  const [hookupTab, setHookupTab] = useState("curl");
  const [testHookupMsg, setTestHookupMsg] = useState("Vanakkam, what are your capabilities in MuriAI?");
  const [testHookupAgent, setTestHookupAgent] = useState("900001");
  const [testHookupLoading, setTestHookupLoading] = useState(false);
  const [testHookupResponse, setTestHookupResponse] = useState(null);
  const [copiedSnippet, setCopiedSnippet] = useState(false);
  const hookupRef = useRef(null);

  const archetypes = [
    { id: "sales",   icon: "💰", title: "SALES CLOSING BOT",      desc: "Conversion engine" },
    { id: "support", icon: "🛡?", title: "CUSTOMER SUPPORT AGENT", desc: "Resolution layer" },
    { id: "copilot", icon: "🚀", title: "PRODUCT COPILOT",        desc: "Feature navigator" },
    { id: "custom",  icon: "✨", title: "CUSTOM ARCHETYPE",       desc: "Build from scratch" },
  ];

  const adminFetch = useCallback(async (url, options = {}) => {
    const token = sessionStorage.getItem("muri_token");
    const headers = {
      ...(token ? { "Authorization": `Bearer ${token}` } : {}),
      ...(options.headers || {})
    };
    const res = await fetch(url, { ...options, headers });
    if (res.status === 401 || res.status === 403) {
      sessionStorage.removeItem("muri_token");
      window.location.href = "/admin-login";
    }
    return res;
  }, []);

  const loadLibrary      = useCallback(async () => { try { const r = await adminFetch(`${API_BASE}/api/admin/library`);        const d = await r.json(); setLibraryFiles(d.files || []); } catch {} }, [adminFetch]);
  const loadUsers        = useCallback(async () => { try { const r = await adminFetch(`${API_BASE}/api/admin/users`);           const d = await r.json(); setUsers(d.users || []); } catch {} }, [adminFetch]);
  const loadActivity     = useCallback(async () => { try { const r = await adminFetch(`${API_BASE}/api/admin/activity`);        const d = await r.json(); setActivity(d.activity || []); } catch {} }, [adminFetch]);
  const loadAgents       = useCallback(async () => { try { const r = await adminFetch(`${API_BASE}/api/admin/agents`);          const d = await r.json(); setDeployedAgents(d.agents || []); } catch {} }, [adminFetch]);
  const loadCoupons      = useCallback(async () => { try { const r = await adminFetch(`${API_BASE}/api/admin/coupons`);         const d = await r.json(); setCoupons(d.coupons || []); } catch {} }, [adminFetch]);
  const loadCouponStats  = useCallback(async () => { try { const r = await adminFetch(`${API_BASE}/api/admin/coupons/stats/graph`); const d = await r.json(); setCouponStats({ daily: d.daily || [], totals: d.totals || [] }); } catch {} }, [adminFetch]);
  const loadPlans        = useCallback(async () => { try { const r = await adminFetch(`${API_BASE}/api/admin/plans`);           const d = await r.json(); setPlans(d.plans || []); } catch {} }, [adminFetch]);
  const loadSubscriptions= useCallback(async () => { try { const r = await adminFetch(`${API_BASE}/api/admin/subscriptions`);  const d = await r.json(); setSubscriptions(d.subscriptions || []); } catch {} }, [adminFetch]);
  const loadEmotions        = useCallback(async () => { try { const r = await adminFetch(`${API_BASE}/api/admin/emotions`);         const d = await r.json(); setEmotions(d.emotions || []); } catch {} }, [adminFetch]);
  const loadEmotionLibrary  = useCallback(async () => { try { const r = await adminFetch(`${API_BASE}/api/admin/emotions/library`); const d = await r.json(); setEmotionLibrary(d.files || []); } catch {} }, [adminFetch]);

  const loadRagMisses = useCallback(async (key) => {
    try {
      const r = await adminFetch(`${API_BASE}/api/admin/rag-misses/${key}`);
      const d = await r.json();
      setRagMisses(d.misses || []);
    } catch {}
  }, [adminFetch]);

  const loadXaiCache = useCallback(async (key) => {
    try {
      const r = await adminFetch(`${API_BASE}/api/admin/rag-cache/${key}?source=xai`);
      const d = await r.json();
      setXaiCache(d.cache || []);
    } catch {}
  }, [adminFetch]);

  useEffect(() => {
    loadLibrary(); loadUsers(); loadActivity(); loadAgents();
    loadCoupons(); loadCouponStats(); loadPlans(); loadSubscriptions(); loadEmotions(); loadEmotionLibrary();
  }, [loadLibrary, loadUsers, loadActivity, loadAgents, loadCoupons, loadCouponStats, loadPlans, loadSubscriptions, loadEmotions, loadEmotionLibrary]);

  useEffect(() => {
    if (activeSection === "ai-agents" && selectedAgentKey) {
      loadRagMisses(selectedAgentKey);
      loadXaiCache(selectedAgentKey);
    } else if (activeSection === "rag-cache" && cacheAgentKey) {
      loadXaiCache(cacheAgentKey);
    }
  }, [selectedAgentKey, cacheAgentKey, activeSection, loadRagMisses, loadXaiCache]);

  const resolveRagMiss = async (missId) => {
    try {
      const r = await adminFetch(`${API_BASE}/api/admin/rag-misses/${missId}/resolve`, { method: "POST" });
      const d = await r.json();
      if (d.success) loadRagMisses(selectedAgentKey);
    } catch {}
  };

  const deleteRagCache = async (cacheId) => {
    if (!window.confirm("Are you sure you want to delete this cached answer?")) return;
    try {
      const r = await adminFetch(`${API_BASE}/api/admin/rag-cache/${cacheId}`, { method: "DELETE" });
      const d = await r.json();
      if (d.success) {
        if (activeSection === "ai-agents") {
          loadXaiCache(selectedAgentKey);
        } else if (activeSection === "rag-cache") {
          loadXaiCache(cacheAgentKey);
        }
      }
    } catch {}
  };

  const handleDeptFileSelect = async (e) => {
    const files = [...e.target.files];
    if (files.length === 0) return;
    setUploading(true);
    setUploadStatus(null);
    const formData = new FormData();
    files.forEach(file => {
      formData.append("pdfs", file);
    });
    formData.append("agentKey", selectedAgentKey);

    try {
      const res = await adminFetch(`${API_BASE}/api/admin/library/upload-multiple`, {
        method: "POST",
        body: formData
      });
      const data = await res.json();
      const okCount = (data.results && Array.isArray(data.results))
        ? data.results.filter(r => r.success).length
        : (data.count || (data.success ? files.length : 0));

      if (data.success && okCount > 0) {
        const details = data.results ? data.results.filter(r => r.success).map(r => r.file).join(", ") : "";
        setUploadStatus({ type: "success", message: `✅ Successfully uploaded ${okCount} document(s)${details ? ": " + details : ""}.` });
        await loadAgents();
        await loadLibrary();
      } else {
        const errorDetails = data.results ? data.results.filter(r => !r.success).map(r => `${r.file}: ${r.error}`).join("; ") : "";
        setUploadStatus({ type: "error", message: data.error || `Upload failed${errorDetails ? ": " + errorDetails : ". Could not extract text from document(s)."}` });
      }
    } catch (err) {
      setUploadStatus({ type: "error", message: err.message });
    } finally {
      setUploading(false);
    }
  };

  const handleDeptFileDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = [...e.dataTransfer.files];
    if (files.length === 0) return;
    setUploading(true);
    setUploadStatus(null);
    const formData = new FormData();
    files.forEach(file => {
      formData.append("pdfs", file);
    });
    formData.append("agentKey", selectedAgentKey);

    try {
      const res = await adminFetch(`${API_BASE}/api/admin/library/upload-multiple`, {
        method: "POST",
        body: formData
      });
      const data = await res.json();
      const okCount = (data.results && Array.isArray(data.results))
        ? data.results.filter(r => r.success).length
        : (data.count || (data.success ? files.length : 0));

      if (data.success && okCount > 0) {
        const details = data.results ? data.results.filter(r => r.success).map(r => `${r.file}${r.fileType ? " (" + r.fileType + ")" : ""}`).join(", ") : "";
        setUploadStatus({ type: "success", message: `✅ Successfully uploaded ${okCount} document(s)${details ? ": " + details : ""}.` });
        await loadAgents();
        await loadLibrary();
      } else {
        const errorDetails = data.results ? data.results.filter(r => !r.success).map(r => `${r.file}: ${r.error}`).join("; ") : "";
        setUploadStatus({ type: "error", message: data.error || `Upload failed${errorDetails ? ": " + errorDetails : ". Could not extract text."}` });
      }
    } catch (err) {
      setUploadStatus({ type: "error", message: err.message });
    } finally {
      setUploading(false);
    }
  };

  const viewFile = async (filename, startEditing = false) => {
    try {
      const res = await adminFetch(`${API_BASE}/api/admin/library/${encodeURIComponent(filename)}/content`);
      const data = await res.json();
      setViewFileModal({
        rawFilename: filename,
        filename: filename.replace(/^\d+_/, ""),
        content: data.content || "(empty)",
        type: data.type,
        isEditing: startEditing,
        saving: false
      });
    } catch (err) {
      alert("Could not load file: " + err.message);
    }
  };

  const handleDownloadFile = (filename) => {
    const token = sessionStorage.getItem("muri_token");
    const url = `${API_BASE}/api/admin/library/${encodeURIComponent(filename)}/download${token ? `?token=${encodeURIComponent(token)}` : ""}`;
    const a = document.createElement("a");
    a.href = url;
    a.download = filename.replace(/^\d+_/, "");
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleSaveFileContent = async () => {
    if (!viewFileModal) return;
    setViewFileModal(prev => ({ ...prev, saving: true }));
    try {
      const res = await adminFetch(`${API_BASE}/api/admin/library/${encodeURIComponent(viewFileModal.rawFilename)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: viewFileModal.content })
      });
      const data = await res.json();
      if (data.success) {
        alert("File updated successfully!");
        if (data.newFilename && data.newFilename !== viewFileModal.rawFilename) {
          setViewFileModal(prev => ({
            ...prev,
            rawFilename: data.newFilename,
            filename: data.newFilename.replace(/^\d+_/, ""),
            type: "md",
            isEditing: false,
            saving: false
          }));
        } else {
          setViewFileModal(prev => ({ ...prev, isEditing: false, saving: false }));
        }
        loadAgents();
        loadLibrary();
      } else {
        alert("Failed to update file: " + (data.error || "Unknown error"));
        setViewFileModal(prev => ({ ...prev, saving: false }));
      }
    } catch (err) {
      alert("Error updating file: " + err.message);
      setViewFileModal(prev => ({ ...prev, saving: false }));
    }
  };

  const handleDeptFileDelete = async (filename) => {
    if (!window.confirm(`Are you sure you want to delete ${filename.replace(/^\d+_/, "")}?`)) return;
    try {
      const res = await adminFetch(`${API_BASE}/api/admin/library/${encodeURIComponent(filename)}`, {
        method: "DELETE"
      });
      const data = await res.json();
      if (data.success) {
        loadAgents();
        loadLibrary();
      }
    } catch {}
  };

  const toggleAgentActive = async () => {
    const selectedAgent = deployedAgents.find(a => a.id === selectedAgentKey);
    if (!selectedAgent) return;
    const url = `${API_BASE}/api/admin/agents/${selectedAgentKey}/${selectedAgent.isActive ? "deactivate" : "activate"}`;
    try {
      const res = await adminFetch(url, { method: "POST" });
      const data = await res.json();
      if (data.success) {
        loadAgents();
      }
    } catch {}
  };

  // ── Agent handlers ─────────────────────────────────────────
  const handleGenerateKnowledgePdf = async () => {
    if (!selectedAgentKey) return;
    setGeneratingKnowledge(true);
    setKnowledgeGenStatus(null);
    try {
      const res = await adminFetch(`${API_BASE}/api/admin/agents/${selectedAgentKey}/generate-knowledge-pdf`, { method: "POST" });
      const data = await res.json();
      if (data.success) {
        setKnowledgeGenStatus({ type: "success", message: `✅ ${data.message} (${data.entriesProcessed} entries → ${data.markdownFile}${data.pdfFile ? " + PDF" : ""})` });
        await loadAgents();
        await loadLibrary();
      } else {
        setKnowledgeGenStatus({ type: "error", message: data.error || "Failed to generate knowledge document." });
      }
    } catch (err) {
      setKnowledgeGenStatus({ type: "error", message: err.message });
    } finally {
      setGeneratingKnowledge(false);
    }
  };

  const uploadPdf = async (file) => {
    if (!file || file.type !== "application/pdf") { setUploadStatus({ type: "error", message: "Only PDF files are supported." }); return; }
    setUploading(true); setUploadStatus(null);
    const formData = new FormData();
    formData.append("pdf", file);
    formData.append("agentName", agentName || selectedArchetype || "Agent");
    try {
      const res  = await adminFetch(`${API_BASE}/api/admin/library/upload`, { method: "POST", body: formData });
      const data = await res.json();
      if (data.success) {
        setUploadStatus({ type: "success", message: `✅ "${data.fileName}" uploaded — ${data.pages} pages indexed.` });
        setPendingPdfFiles(prev => [...prev, data.savedAs]);
        loadLibrary();
      } else setUploadStatus({ type: "error", message: data.error || "Upload failed." });
    } catch (err) { setUploadStatus({ type: "error", message: "Upload error: " + err.message }); }
    finally { setUploading(false); }
  };

  // Upload multiple PDFs to an EXISTING agent
  const uploadPdfsToAgent = async (files, agentKey) => {
    if (!files || files.length === 0) return;
    setUploading(true); setUploadStatus(null);
    const formData = new FormData();
    for (const f of files) formData.append("pdfs", f);
    formData.append("agentKey", agentKey);
    try {
      const res = await adminFetch(`${API_BASE}/api/admin/library/upload-multiple`, { method: "POST", body: formData });
      const data = await res.json();
      if (data.success) {
        const ok = data.results.filter(r => r.success).length;
        const fail = data.results.filter(r => !r.success).length;
        setUploadStatus({ type: "success", message: `✅ ${ok} PDF${ok !== 1 ? "s" : ""} uploaded to agent.${fail > 0 ? ` (${fail} failed)` : ""}` });
        loadAgents(); loadLibrary();
      } else setUploadStatus({ type: "error", message: data.error || "Upload failed." });
    } catch (err) { setUploadStatus({ type: "error", message: "Upload error: " + err.message }); }
    finally { setUploading(false); }
  };
  const handleDrop       = (e) => { e.preventDefault(); setDragOver(false); const files = [...e.dataTransfer.files].filter(f => f.type === "application/pdf"); files.forEach(f => uploadPdf(f)); };
  const handleFileSelect = (e) => { const files = [...e.target.files]; files.forEach(f => uploadPdf(f)); e.target.value = ""; };
  const deleteFile = async (filename) => {
    if (!window.confirm(`Delete "${filename}"?`)) return;
    try { await adminFetch(`${API_BASE}/api/admin/library/${encodeURIComponent(filename)}`, { method: "DELETE" }); loadLibrary(); } catch {}
  };
  const deployAgent = async () => {
    if (!selectedArchetype) return alert("Please select an agent archetype.");
    if (!agentName.trim()) return alert("Please enter an agent name.");
    const name = agentName.trim();
    let archetype = archetypes.find(a => a.id === selectedArchetype);
    if (selectedArchetype === "custom") {
      if (!customArchetypeTitle.trim()) return alert("Please enter a custom archetype title.");
      archetype = {
        id: "custom",
        icon: customArchetypeIcon || "🤖",
        title: customArchetypeTitle.trim().toUpperCase(),
        desc: customArchetypeDesc.trim() || "Custom configured agent"
      };
    }
    const newAgent = { id: Date.now(), name, archetype, pdfs: pendingPdfFiles.length, pdfFiles: pendingPdfFiles, deployedAt: new Date().toLocaleString() };
    try {
      const res = await adminFetch(`${API_BASE}/api/admin/agents`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(newAgent) });
      const data = await res.json();
      if (data.success) {
        await loadAgents();
        setUploadStatus({ type: "success", message: `🚀 Agent "${name}" deployed successfully!` });
        setAgentName(""); setSelectedArchetype(null); setPendingPdfFiles([]);
        setCustomArchetypeTitle(""); setCustomArchetypeDesc(""); setCustomArchetypeIcon("🤖");
      } else {
        setUploadStatus({ type: "error", message: data.error || "Deploy failed." });
      }
    } catch { setUploadStatus({ type: "error", message: "Deploy failed." }); }
  };
  const setActiveAgent = async (agentId) => {
    try { await adminFetch(`${API_BASE}/api/admin/agents/${agentId}/activate`, { method: "POST" }); await loadAgents(); } catch {}
  };
  const deactivateAgent = async (agentId) => {
    try { await adminFetch(`${API_BASE}/api/admin/agents/${agentId}/deactivate`, { method: "POST" }); await loadAgents(); } catch {}
  };
  const deleteAgent = async (agent) => {
    if (!window.confirm(`Delete agent "${agent.name}"?`)) return;
    try { await adminFetch(`${API_BASE}/api/admin/agents/${agent.id}`, { method: "DELETE" }); await loadAgents(); await loadLibrary(); } catch {}
  };

  // ── Coupon handlers ────────────────────────────────────────
  const createCoupon = async () => {
    const { code, discount_pct, max_uses, applicable_plan, expires_at } = couponForm;
    if (!code.trim() || !discount_pct) { setCouponStatus({ type: "error", message: "Code and discount % are required." }); return; }
    try {
      const res = await adminFetch(`${API_BASE}/api/admin/coupons`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, discount_pct: parseInt(discount_pct), max_uses: parseInt(max_uses) || 30, applicable_plan: applicable_plan || null, expires_at: expires_at || null }),
      });
      const data = await res.json();
      if (data.success) {
        setCouponStatus({ type: "success", message: `✅ Coupon "${code.toUpperCase()}" created!` });
        setCouponForm({ code: "", discount_pct: "", max_uses: 30, applicable_plan: "", expires_at: "" });
        loadCoupons(); loadCouponStats();
      } else setCouponStatus({ type: "error", message: data.error || "Failed." });
    } catch (err) { setCouponStatus({ type: "error", message: err.message }); }
  };

  const toggleCoupon = async (id) => {
    try { await adminFetch(`${API_BASE}/api/admin/coupons/${id}/toggle`, { method: "PATCH" }); loadCoupons(); } catch {}
  };

  const deleteCoupon = async (id, code) => {
    if (!window.confirm(`Delete coupon "${code}"? All usage history will be removed.`)) return;
    try { await adminFetch(`${API_BASE}/api/admin/coupons/${id}`, { method: "DELETE" }); loadCoupons(); loadCouponStats(); } catch {}
  };

  const viewCouponUsages = async (coupon) => {
    setShowUsagesFor(coupon);
    try {
      const r = await adminFetch(`${API_BASE}/api/admin/coupons/${coupon.id}/usages`);
      const d = await r.json();
      setCouponUsages(d.usages || []);
    } catch {}
  };

  // ── Plan handlers ──────────────────────────────────────────
  const savePlan = async () => {
    const { name, chat_limit, price, description } = planForm;
    if (!name || !chat_limit || !price) { setPlanStatus({ type: "error", message: "Name, chat limit and price are required." }); return; }
    try {
      const res = await adminFetch(`${API_BASE}/api/admin/plans`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, chat_limit: parseInt(chat_limit), price: parseFloat(price), description }),
      });
      const data = await res.json();
      if (data.success) {
        setPlanStatus({ type: "success", message: "✅ Plan saved!" });
        setPlanForm({ name: "", chat_limit: "", price: "", description: "" });
        loadPlans();
      } else setPlanStatus({ type: "error", message: data.error || "Failed." });
    } catch (err) { setPlanStatus({ type: "error", message: err.message }); }
  };

  const deletePlan = async (id, name) => {
    if (!window.confirm(`Disable plan "${name}"?`)) return;
    try { await adminFetch(`${API_BASE}/api/admin/plans/${id}`, { method: "DELETE" }); loadPlans(); } catch {}
  };

  // ── Developer API Keys handlers ───────────────────────────
  const loadApiKeys = useCallback(async () => {
    setIsRefreshingApiKeys(true);
    try {
      const res = await adminFetch(`${API_BASE}/api/admin/api-keys`);
      const data = await res.json();
      const keys = data.keys || [];
      setApiKeys(keys);
      setSelectedHookupKey(prev => {
        if (prev && keys.some(k => k.api_key === prev)) return prev;
        return keys[0]?.api_key || "";
      });
    } catch (err) {
      console.error("Failed to load API keys:", err);
    } finally {
      setTimeout(() => setIsRefreshingApiKeys(false), 400);
    }
  }, [adminFetch]);

  useEffect(() => {
    if (activeSection === "api") {
      loadApiKeys();
    }
  }, [activeSection, loadApiKeys]);

  const createApiKey = async () => {
    if (!apiKeyName.trim()) {
      setApiKeyStatus({ type: "error", message: "Please enter a key name (e.g. 'Production Webhook', 'Mobile App')." });
      return;
    }
    try {
      const res = await adminFetch(`${API_BASE}/api/admin/api-keys`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: apiKeyName.trim() }),
      });
      const data = await res.json();
      if (data.success) {
        setApiKeyStatus({ type: "success", message: `✅ Generated new key "${data.key.name}"! Plugged into External Hookup Guide below.` });
        setApiKeyName("");
        setRevealedKeys(prev => ({ ...prev, [data.key.id]: true }));
        setSelectedHookupKey(data.key.api_key);
        loadApiKeys();
        setTimeout(() => {
          if (hookupRef.current) {
            hookupRef.current.scrollIntoView({ behavior: "smooth" });
          }
        }, 150);
      } else {
        setApiKeyStatus({ type: "error", message: data.error || "Failed to create API key." });
      }
    } catch (err) {
      setApiKeyStatus({ type: "error", message: err.message });
    }
  };

  const runHookupTest = async () => {
    if (!selectedHookupKey) {
      alert("Please select or create an API key first.");
      return;
    }
    if (!testHookupMsg.trim()) return;
    setTestHookupLoading(true);
    setTestHookupResponse(null);
    const startTime = Date.now();
    try {
      const res = await fetch(`${API_BASE}/api/v1/chat`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${selectedHookupKey}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          message: testHookupMsg.trim(),
          agentKey: parseInt(testHookupAgent, 10) || 900001
        })
      });
      const data = await res.json();
      const latency = Date.now() - startTime;
      setTestHookupResponse({ ok: res.ok, status: res.status, data, latency });
    } catch (err) {
      setTestHookupResponse({ ok: false, error: err.message, latency: Date.now() - startTime });
    } finally {
      setTestHookupLoading(false);
    }
  };

  const copySnippet = (code) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code);
    } else {
      const el = document.createElement("textarea");
      el.value = code;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    setCopiedSnippet(true);
    setTimeout(() => setCopiedSnippet(false), 2200);
  };

  const toggleApiKey = async (id) => {
    try {
      await adminFetch(`${API_BASE}/api/admin/api-keys/${id}/toggle`, { method: "PATCH" });
      loadApiKeys();
    } catch {}
  };

  const deleteApiKey = async (id, name) => {
    if (!window.confirm(`Revoke and permanently delete API key "${name}"? Applications using this key will immediately lose access.`)) return;
    try {
      await adminFetch(`${API_BASE}/api/admin/api-keys/${id}`, { method: "DELETE" });
      loadApiKeys();
    } catch {}
  };

  const copyToClipboard = (text, id) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text);
    } else {
      const el = document.createElement("textarea");
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
    }
    setCopiedKeyId(id);
    setTimeout(() => setCopiedKeyId(null), 2000);
  };

  // ── Styles ─────────────────────────────────────────────────
  const navItems = [
    { id: "overview",      icon: "⊞",  label: "Overview" },
    { id: "analytics",     icon: "📊",  label: "Analytics" },
    { id: "tenants",       icon: "�?�",  label: "Tenants",         group: "PLATFORM" },
    { id: "users",         icon: "👤",  label: "Users" },
    { id: "ai-agents",     icon: "🤖",  label: "AI Agents" },
    { id: "conversations", icon: "💬",  label: "Conversations" },
    { id: "plans",         icon: "💳",  label: "Plans & Billing" },
    { id: "coupons",       icon: "�?��?", label: "Coupons" },
    { id: "kyc",           icon: "🔑",  label: "KYC details",     group: "SECURITY" },
    { id: "security",      icon: "🔒",  label: "Security" },
    { id: "infra",         icon: "🖥�?", label: "Infrastructure",  group: "DEVOPS" },
    { id: "api",           icon: "⚡",  label: "API & Dev" },
    { id: "notifs",        icon: "🔔",  label: "Notifications" },
    { id: "settings",      icon: "⚙�?", label: "Settings" },
    { id: "emotions",      icon: "😊",  label: "Emotions",        group: "AI CONFIG" },
    { id: "rag-cache",     icon: "🧠",  label: "RAG Cache" },
  ];

  const s = {
    root:       { display: "flex", height: "100vh", background: "#0f1117", color: "#e2e8f0", fontFamily: "'Inter',-apple-system,sans-serif", fontSize: 13, overflow: "hidden" },
    sidebar:    { width: 200, background: "#0d0f16", borderRight: "1px solid rgba(255,255,255,0.06)", display: "flex", flexDirection: "column", padding: "16px 0", overflowY: "auto", flexShrink: 0 },
    brand:      { padding: "0 16px 20px", fontWeight: 700, fontSize: 15, display: "flex", alignItems: "center", gap: 8, color: "#f8fafc" },
    brandDot:   { width: 8, height: 8, borderRadius: "50%", background: "#6366f1" },
    groupLabel: { padding: "12px 16px 4px", fontSize: 10, letterSpacing: "0.1em", color: "#475569", fontWeight: 600 },
    navItem:    (active) => ({ display: "flex", alignItems: "center", gap: 9, padding: "8px 16px", cursor: "pointer", borderRadius: 6, margin: "1px 8px", background: active ? "rgba(99,102,241,0.15)" : "transparent", color: active ? "#a5b4fc" : "#94a3b8", fontWeight: active ? 600 : 400, transition: "all 0.15s" }),
    main:       { flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" },
    topbar:     { padding: "14px 24px", borderBottom: "1px solid rgba(255,255,255,0.06)", background: "#0d0f16", display: "flex", alignItems: "center", justifyContent: "space-between" },
    content:    { flex: 1, overflowY: "auto", padding: "24px" },
    card:       { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 14, padding: 20, marginBottom: 16 },
    sectionTitle: { fontSize: 10, letterSpacing: "0.12em", color: "#64748b", fontWeight: 700, marginBottom: 14, display: "flex", alignItems: "center", gap: 8 },
    btn:        { background: "#6366f1", color: "#fff", border: "none", borderRadius: 8, padding: "10px 20px", fontWeight: 600, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, transition: "opacity 0.15s" },
    input:      { background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, padding: "9px 12px", color: "#e2e8f0", fontSize: 13, width: "100%", outline: "none", boxSizing: "border-box" },
    badge:      (color) => ({ background: `${color}22`, border: `1px solid ${color}44`, color, borderRadius: 4, padding: "2px 8px", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", whiteSpace: "nowrap" }),
    statusBox:  (type) => ({ padding: "10px 14px", borderRadius: 8, fontSize: 12, background: type === "success" ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)", border: `1px solid ${type === "success" ? "rgba(34,197,94,0.3)" : "rgba(239,68,68,0.3)"}`, color: type === "success" ? "#4ade80" : "#f87171", marginTop: 10 }),
  };

  const planIcon = (chatLimit) => {
    if (chatLimit === -1) return "♾�?";
    if (chatLimit <= 20) return "🌱";
    if (chatLimit <= 50) return "🚀";
    return "⚡";
  };

  // ── Section Renders ────────────────────────────────────────

  const renderCoupons = () => (
    <div>
      {/* Stats Row */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 20 }}>
        {[
          { label: "Total Coupons", value: coupons.length, color: "#6366f1", icon: "�?��?" },
          { label: "Active Coupons", value: coupons.filter(c => c.is_active).length, color: "#22c55e", icon: "✅" },
          { label: "Total Redemptions", value: coupons.reduce((a, c) => a + (c.actual_uses || 0), 0), color: "#f59e0b", icon: "🎟�?" },
          { label: "Avg. Discount", value: coupons.length ? Math.round(coupons.reduce((a, c) => a + c.discount_pct, 0) / coupons.length) + "%" : "0%", color: "#ec4899", icon: "%" },
        ].map((stat, i) => (
          <div key={i} style={{ ...s.card, marginBottom: 0, textAlign: "center", padding: 16 }}>
            <div style={{ fontSize: 22 }}>{stat.icon}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: stat.color, marginTop: 4 }}>{stat.value}</div>
            <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>{stat.label}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
        {/* Create Coupon Form */}
        <div style={s.card}>
          <div style={s.sectionTitle}>�?��? CREATE NEW COUPON</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <input style={s.input} placeholder="Coupon Code (e.g. SAVE20)" value={couponForm.code}
              onChange={e => setCouponForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div>
                <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>Discount %</div>
                <input style={s.input} type="number" min="1" max="99" placeholder="e.g. 20" value={couponForm.discount_pct}
                  onChange={e => setCouponForm(f => ({ ...f, discount_pct: e.target.value }))} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>Max Uses</div>
                <input style={s.input} type="number" min="1" placeholder="30" value={couponForm.max_uses}
                  onChange={e => setCouponForm(f => ({ ...f, max_uses: e.target.value }))} />
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>Apply to Plan (optional — leave blank for all)</div>
              <select style={{ ...s.input, appearance: "none" }} value={couponForm.applicable_plan}
                onChange={e => setCouponForm(f => ({ ...f, applicable_plan: e.target.value }))}>
                <option value="">All Plans</option>
                {plans.map(p => <option key={p.id} value={p.id}>{p.name} ({p.chat_limit === -1 ? "Unlimited" : p.chat_limit + " chats"})</option>)}
              </select>
            </div>
            <div>
              <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>Expiry Date (optional)</div>
              <input style={s.input} type="datetime-local" value={couponForm.expires_at}
                onChange={e => setCouponForm(f => ({ ...f, expires_at: e.target.value }))} />
            </div>
            <button style={{ ...s.btn, justifyContent: "center" }} onClick={createCoupon}>
              ➕ Create Coupon
            </button>
            {couponStatus && <div style={s.statusBox(couponStatus.type)}>{couponStatus.message}</div>}
          </div>
        </div>

        {/* Usage Graph */}
        <div style={s.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div style={s.sectionTitle}>📊 COUPON USAGE — PER COUPON</div>
          </div>
          {couponStats.totals.length === 0 ? (
            <div style={{ color: "#475569", fontSize: 12, textAlign: "center", padding: 40 }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📉</div>
              No coupon redemptions yet
            </div>
          ) : (
            <>
              <BarChart
                data={couponStats.totals.map(t => ({ label: t.coupon_code, value: t.total_uses }))}
                label="Redemptions per coupon"
              />
              <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 7 }}>
                {couponStats.totals.map((t, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 11, padding: "6px 10px", background: "rgba(255,255,255,0.03)", borderRadius: 8, border: "1px solid rgba(255,255,255,0.05)" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ width: 10, height: 10, borderRadius: 2, background: CHART_COLORS[i % CHART_COLORS.length] }} />
                      <span style={{ fontWeight: 700, color: "#e2e8f0" }}>{t.coupon_code}</span>
                      <span style={{ color: "#64748b" }}>{t.discount_pct}% off</span>
                    </div>
                    <div style={{ display: "flex", gap: 16 }}>
                      <span style={{ color: "#a5b4fc", fontWeight: 600 }}>{t.total_uses} uses</span>
                      {t.total_discount_given > 0 && <span style={{ color: "#4ade80" }}>{formatINR(t.total_discount_given)} saved</span>}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Daily Usage Timeline */}
      {couponStats.daily.length > 0 && (
        <div style={{ ...s.card, marginBottom: 16 }}>
          <div style={s.sectionTitle}>📈 DAILY USAGE — LAST 30 DAYS</div>
          {(() => {
            const byDate = {};
            couponStats.daily.forEach(r => {
              const dt = r.usage_date?.slice(0, 10) || "?";
              byDate[dt] = (byDate[dt] || 0) + r.uses_count;
            });
            const days = Object.keys(byDate).sort().slice(-30);
            return (
              <LineChart
                data={days.map(d => ({ label: d.slice(5), value: byDate[d] }))}
                label="Total daily coupon redemptions"
              />
            );
          })()}
        </div>
      )}

      {/* Coupons Table */}
      <div style={s.card}>
        <div style={s.sectionTitle}>🗂�? ALL COUPONS — {coupons.length}</div>
        {coupons.length === 0 ? (
          <div style={{ color: "#475569", fontSize: 12, textAlign: "center", padding: 30 }}>No coupons created yet.</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ color: "#475569", fontSize: 11, letterSpacing: "0.06em" }}>
                <th style={{ textAlign: "left", padding: "6px 8px", fontWeight: 600 }}>CODE</th>
                <th style={{ textAlign: "center", padding: "6px 8px", fontWeight: 600 }}>DISCOUNT</th>
                <th style={{ textAlign: "center", padding: "6px 8px", fontWeight: 600 }}>USES</th>
                <th style={{ textAlign: "center", padding: "6px 8px", fontWeight: 600 }}>PLAN</th>
                <th style={{ textAlign: "center", padding: "6px 8px", fontWeight: 600 }}>STATUS</th>
                <th style={{ textAlign: "center", padding: "6px 8px", fontWeight: 600 }}>EXPIRES</th>
                <th style={{ textAlign: "center", padding: "6px 8px", fontWeight: 600 }}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {coupons.map(c => {
                const pct = Math.round(((c.actual_uses || 0) / c.max_uses) * 100);
                const exhausted = (c.actual_uses || 0) >= c.max_uses;
                const expired = c.expires_at && new Date(c.expires_at) < new Date();
                return (
                  <tr key={c.id} style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                    <td style={{ padding: "10px 8px" }}>
                      <span style={{ fontWeight: 700, color: "#a5b4fc", fontSize: 13, letterSpacing: "0.04em" }}>{c.code}</span>
                    </td>
                    <td style={{ padding: "10px 8px", textAlign: "center" }}>
                      <span style={s.badge("#f59e0b")}>{c.discount_pct}% OFF</span>
                    </td>
                    <td style={{ padding: "10px 8px", textAlign: "center" }}>
                      <div style={{ fontSize: 12 }}>{c.actual_uses || 0} / {c.max_uses}</div>
                      <div style={{ height: 3, background: "rgba(255,255,255,0.1)", borderRadius: 2, marginTop: 3, overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${Math.min(pct, 100)}%`, background: exhausted ? "#ef4444" : "#6366f1", borderRadius: 2 }} />
                      </div>
                    </td>
                    <td style={{ padding: "10px 8px", textAlign: "center", fontSize: 11, color: "#94a3b8" }}>
                      {c.plan_name || "All Plans"}
                    </td>
                    <td style={{ padding: "10px 8px", textAlign: "center" }}>
                      {exhausted ? <span style={s.badge("#ef4444")}>EXHAUSTED</span>
                        : expired ? <span style={s.badge("#f97316")}>EXPIRED</span>
                        : c.is_active ? <span style={s.badge("#22c55e")}>ACTIVE</span>
                        : <span style={s.badge("#64748b")}>PAUSED</span>}
                    </td>
                    <td style={{ padding: "10px 8px", textAlign: "center", fontSize: 11, color: "#64748b" }}>
                      {c.expires_at ? new Date(c.expires_at).toLocaleDateString() : "—"}
                    </td>
                    <td style={{ padding: "10px 8px", textAlign: "center" }}>
                      <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
                        <button onClick={() => viewCouponUsages(c)} title="View users"
                          style={{ background: "rgba(99,102,241,0.15)", border: "1px solid rgba(99,102,241,0.3)", borderRadius: 5, color: "#a5b4fc", cursor: "pointer", fontSize: 11, padding: "3px 8px", fontWeight: 600 }}>
                          👥 Users
                        </button>
                        <button onClick={() => toggleCoupon(c.id)} title={c.is_active ? "Pause" : "Activate"}
                          style={{ background: "rgba(251,191,36,0.1)", border: "1px solid rgba(251,191,36,0.3)", borderRadius: 5, color: "#fbbf24", cursor: "pointer", fontSize: 11, padding: "3px 8px" }}>
                          {c.is_active ? "�?�" : "▶"}
                        </button>
                        <button onClick={() => deleteCoupon(c.id, c.code)} title="Delete"
                          style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 5, color: "#f87171", cursor: "pointer", fontSize: 11, padding: "3px 8px" }}>
                          🗑
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Coupon Usages Modal */}
      {showUsagesFor && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => setShowUsagesFor(null)}>
          <div style={{ background: "#13151f", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 16, padding: 24, width: 560, maxHeight: "80vh", overflowY: "auto" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15 }}>👥 Users for <span style={{ color: "#a5b4fc" }}>{showUsagesFor.code}</span></div>
                <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>{showUsagesFor.discount_pct}% discount · {couponUsages.length} total uses</div>
              </div>
              <button onClick={() => setShowUsagesFor(null)}
                style={{ background: "none", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#94a3b8", cursor: "pointer", padding: "4px 12px" }}>✕</button>
            </div>
            {couponUsages.length === 0 ? (
              <div style={{ color: "#475569", textAlign: "center", padding: 30 }}>No usage recorded yet.</div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ color: "#475569", fontSize: 11 }}>
                    <th style={{ textAlign: "left", padding: "6px 0", fontWeight: 600 }}>USERNAME</th>
                    <th style={{ textAlign: "left", padding: "6px 0", fontWeight: 600 }}>PLAN</th>
                    <th style={{ textAlign: "right", padding: "6px 0", fontWeight: 600 }}>ORIGINAL</th>
                    <th style={{ textAlign: "right", padding: "6px 0", fontWeight: 600 }}>PAID</th>
                    <th style={{ textAlign: "right", padding: "6px 0", fontWeight: 600 }}>DATE</th>
                  </tr>
                </thead>
                <tbody>
                  {couponUsages.map((u, i) => (
                    <tr key={i} style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                      <td style={{ padding: "8px 0", fontWeight: 600 }}>{u.username}</td>
                      <td style={{ padding: "8px 0", color: "#94a3b8", fontSize: 11 }}>{u.plan_name}</td>
                      <td style={{ padding: "8px 0", textAlign: "right", color: "#64748b", fontSize: 11 }}>{formatINR(u.original_price)}</td>
                      <td style={{ padding: "8px 0", textAlign: "right", color: "#4ade80", fontWeight: 600 }}>{formatINR(u.final_price)}</td>
                      <td style={{ padding: "8px 0", textAlign: "right", color: "#64748b", fontSize: 11 }}>{new Date(u.used_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderPlans = () => (
    <div>
      {/* Plans Grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 14, marginBottom: 20 }}>
        {plans.map(p => {
          const subs = subscriptions.filter(s => s.plan_id === p.id);
          return (
            <div key={p.id} style={{ ...s.card, marginBottom: 0, background: "rgba(99,102,241,0.06)", border: "1px solid rgba(99,102,241,0.2)", position: "relative" }}>
              <div style={{ fontSize: 28, marginBottom: 6 }}>{planIcon(p.chat_limit)}</div>
              <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 2 }}>{p.name}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: "#a5b4fc", marginBottom: 4 }}>{formatINR(p.price)}<span style={{ fontSize: 11, fontWeight: 400, color: "#64748b" }}>/mo</span></div>
              <div style={{ fontSize: 11, color: "#64748b", marginBottom: 8 }}>{p.chat_limit === -1 ? "♾�? Unlimited chats" : `💬 ${p.chat_limit} chats/month`}</div>
              <div style={{ fontSize: 11, color: "#94a3b8", marginBottom: 10 }}>{p.description}</div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={s.badge("#22c55e")}>{subs.length} subscriber{subs.length !== 1 ? "s" : ""}</span>
                <button onClick={() => deletePlan(p.id, p.name)}
                  style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontSize: 12 }}>🗑</button>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {/* Create Plan Form */}
        <div style={s.card}>
          <div style={s.sectionTitle}>➕ CREATE / EDIT PLAN</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <input style={s.input} placeholder="Plan Name (e.g. Professional)" value={planForm.name}
              onChange={e => setPlanForm(f => ({ ...f, name: e.target.value }))} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <div>
                <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>Chat Limit (-1 = unlimited)</div>
                <input style={s.input} type="number" placeholder="e.g. 50" value={planForm.chat_limit}
                  onChange={e => setPlanForm(f => ({ ...f, chat_limit: e.target.value }))} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>Price (₹)</div>
                <input style={s.input} type="number" placeholder="e.g. 699" value={planForm.price}
                  onChange={e => setPlanForm(f => ({ ...f, price: e.target.value }))} />
              </div>
            </div>
            <input style={s.input} placeholder="Description" value={planForm.description}
              onChange={e => setPlanForm(f => ({ ...f, description: e.target.value }))} />
            <button style={{ ...s.btn, justifyContent: "center" }} onClick={savePlan}>
              💾 Save Plan
            </button>
            {planStatus && <div style={s.statusBox(planStatus.type)}>{planStatus.message}</div>}
          </div>
        </div>

        {/* Subscriptions */}
        <div style={s.card}>
          <div style={s.sectionTitle}>👥 ACTIVE SUBSCRIPTIONS — {subscriptions.length}</div>
          {subscriptions.length === 0 ? (
            <div style={{ color: "#475569", fontSize: 12, textAlign: "center", padding: 30 }}>No subscriptions yet.</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ color: "#475569", fontSize: 11 }}>
                  <th style={{ textAlign: "left", padding: "5px 0", fontWeight: 600 }}>USER</th>
                  <th style={{ textAlign: "left", padding: "5px 0", fontWeight: 600 }}>PLAN</th>
                  <th style={{ textAlign: "center", padding: "5px 0", fontWeight: 600 }}>CHATS</th>
                  <th style={{ textAlign: "center", padding: "5px 0", fontWeight: 600 }}>COUPON</th>
                </tr>
              </thead>
              <tbody>
                {subscriptions.slice(0, 20).map((s2, i) => (
                  <tr key={i} style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                    <td style={{ padding: "7px 0", fontWeight: 600, fontSize: 12 }}>{s2.username}</td>
                    <td style={{ padding: "7px 0", fontSize: 11, color: "#94a3b8" }}>{s2.plan_name}</td>
                    <td style={{ padding: "7px 0", textAlign: "center", fontSize: 11 }}>
                      {s2.chats_used}/{s2.chat_limit === -1 ? "∞" : s2.chat_limit}
                    </td>
                    <td style={{ padding: "7px 0", textAlign: "center" }}>
                      {s2.coupon_code ? <span style={s.badge("#f59e0b")}>{s2.coupon_code}</span> : <span style={{ color: "#475569" }}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );


  // ── Emotions PDF Upload & Management ───────────────────────
  const emotionFileInputRef = React.useRef(null);

  const uploadEmotionPdf = async (file) => {
    if (!file || file.type !== "application/pdf") {
      setEmotionStatus({ type: "error", message: "Only PDF files allowed." }); return;
    }
    setEmotionUploading(true);
    setEmotionStatus({ type: "info", message: "�?� Reading PDF & extracting emotions via AI..." });
    const formData = new FormData();
    formData.append("pdf", file);
    try {
      const res = await adminFetch(`${API_BASE}/api/admin/emotions/upload-pdf`, { method: "POST", body: formData });
      const d = await res.json();
      if (res.ok) {
        setEmotionStatus({ type: "success", message: `✅ ${d.added} emotions imported, ${d.skipped} skipped (duplicates).` });
        loadEmotions();
        loadEmotionLibrary();
      } else {
        setEmotionStatus({ type: "error", message: d.error || "Upload failed." });
      }
    } catch (err) {
      setEmotionStatus({ type: "error", message: "Error: " + err.message });
    } finally {
      setEmotionUploading(false);
    }
  };

  const deleteEmotion = async (id) => {
    if (!window.confirm("Delete this emotion?")) return;
    await adminFetch(`${API_BASE}/api/admin/emotions/${id}`, { method: "DELETE" });
    loadEmotions();
  };

  const deleteEmotionPdf = async (filename) => {
    if (!window.confirm(`Delete emotion PDF "${filename.replace(/^\d+_/, "")}"?\n\nNote: Emotions already parsed from this PDF will remain in the DB.`)) return;
    await adminFetch(`${API_BASE}/api/admin/emotions/library/${encodeURIComponent(filename)}`, { method: "DELETE" });
    loadEmotionLibrary();
  };

  const clearAllEmotions = async () => {
    if (!window.confirm("Delete ALL emotions? Chat will fall back to AI for all responses.")) return;
    await fetch(`${API_BASE}/api/admin/emotions/all`, { method: "DELETE" });
    loadEmotions();
    setEmotionStatus({ type: "success", message: "✅ All emotions cleared." });
  };

  const renderEmotions = () => {
    const filtered = emotions.filter(e =>
      e.trigger_key.toLowerCase().includes(emotionSearch.toLowerCase()) ||
      e.response.toLowerCase().includes(emotionSearch.toLowerCase())
    );
    return (
      <div style={{ maxWidth: 860 }}>
        {/* Header */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 10, letterSpacing: "0.12em", color: "#64748b", fontWeight: 700, marginBottom: 4 }}>
            😊 EMOTION RESPONSES — {emotions.length} TOTAL
          </div>
          <div style={{ color: "#94a3b8", fontSize: 12 }}>
            Upload a PDF containing emotion triggers &amp; responses. They will be saved to the Emotion Library and parsed automatically.
          </div>
        </div>

        {/* PDF Upload Zone */}
        <div
          onDragOver={e => { e.preventDefault(); setEmotionDragOver(true); }}
          onDragLeave={() => setEmotionDragOver(false)}
          onDrop={e => { e.preventDefault(); setEmotionDragOver(false); const f = e.dataTransfer.files[0]; if (f) uploadEmotionPdf(f); }}
          onClick={() => !emotionUploading && emotionFileInputRef.current?.click()}
          style={{
            border: `2px dashed ${emotionDragOver ? "#6366f1" : "rgba(255,255,255,0.12)"}`,
            borderRadius: 12, padding: "32px 24px", textAlign: "center", cursor: emotionUploading ? "default" : "pointer",
            background: emotionDragOver ? "rgba(99,102,241,0.08)" : "rgba(255,255,255,0.03)",
            marginBottom: 16, transition: "all 0.2s"
          }}>
          <input ref={emotionFileInputRef} type="file" accept=".pdf" style={{ display: "none" }}
            onChange={e => { const f = e.target.files[0]; if (f) uploadEmotionPdf(f); e.target.value = ""; }} />
          {emotionUploading ? (
            <>
              <div style={{ fontSize: 28, marginBottom: 8 }}>�?�</div>
              <div style={{ color: "#94a3b8", fontSize: 13 }}>Reading PDF and extracting emotions…</div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 32, marginBottom: 8 }}>📄</div>
              <div style={{ fontWeight: 600, marginBottom: 4, color: "#e2e8f0" }}>Drop Emotions PDF here</div>
              <div style={{ color: "#64748b", fontSize: 12 }}>Any format — Claude will parse trigger keywords & responses automatically</div>
            </>
          )}
        </div>

        {/* Status */}
        {emotionStatus && (
          <div style={{
            marginBottom: 16, padding: "10px 16px", borderRadius: 8, fontSize: 13,
            background: emotionStatus.type === "success" ? "rgba(74,222,128,0.1)" : emotionStatus.type === "error" ? "rgba(248,113,113,0.1)" : "rgba(99,102,241,0.1)",
            color: emotionStatus.type === "success" ? "#4ade80" : emotionStatus.type === "error" ? "#f87171" : "#a5b4fc",
            border: `1px solid ${emotionStatus.type === "success" ? "rgba(74,222,128,0.2)" : emotionStatus.type === "error" ? "rgba(248,113,113,0.2)" : "rgba(99,102,241,0.2)"}`
          }}>
            {emotionStatus.message}
          </div>
        )}

        {/* Emotion Library — uploaded PDFs */}
        {emotionLibrary.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 10, letterSpacing: "0.10em", color: "#64748b", fontWeight: 700, marginBottom: 10 }}>
              📚 EMOTION LIBRARY — {emotionLibrary.length} PDF{emotionLibrary.length !== 1 ? "s" : ""} STORED
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {emotionLibrary.map(f => (
                <div key={f.filename} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "rgba(99,102,241,0.05)", border: "1px solid rgba(99,102,241,0.15)", borderRadius: 8, padding: "8px 14px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 16 }}>📄</span>
                    <div>
                      <div style={{ color: "#e2e8f0", fontSize: 12, fontWeight: 600 }}>{f.displayName}</div>
                      <div style={{ color: "#64748b", fontSize: 11 }}>{(f.size / 1024).toFixed(1)} KB · {new Date(f.uploadedAt).toLocaleDateString()}</div>
                    </div>
                  </div>
                  <button onClick={() => deleteEmotionPdf(f.filename)}
                    style={{ background: "rgba(239,68,68,0.12)", color: "#f87171", border: "none", borderRadius: 6, padding: "5px 10px", cursor: "pointer", fontSize: 11, fontWeight: 700 }}>
                    DEL
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Search + Clear */}
        {emotions.length > 0 && (
          <div style={{ display: "flex", gap: 10, marginBottom: 14 }}>
            <input
              value={emotionSearch}
              onChange={e => setEmotionSearch(e.target.value)}
              placeholder="�? Search triggers or responses..."
              style={{ flex: 1, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, padding: "9px 14px", color: "#e2e8f0", fontSize: 13 }}
            />
            <button onClick={clearAllEmotions}
              style={{ background: "rgba(239,68,68,0.12)", color: "#f87171", border: "1px solid rgba(239,68,68,0.2)", borderRadius: 8, padding: "9px 16px", cursor: "pointer", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>
              🗑 CLEAR ALL
            </button>
          </div>
        )}

        {/* Table */}
        {emotions.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#64748b", fontSize: 13 }}>
            No emotions loaded. Upload a PDF to get started.
          </div>
        ) : (
          <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                  <th style={{ textAlign: "left", padding: "10px 16px", fontSize: 10, color: "#64748b", fontWeight: 700, letterSpacing: "0.1em", width: 180 }}>TRIGGER</th>
                  <th style={{ textAlign: "left", padding: "10px 16px", fontSize: 10, color: "#64748b", fontWeight: 700, letterSpacing: "0.1em" }}>RESPONSE</th>
                  <th style={{ padding: "10px 16px", width: 60 }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((em, i) => (
                  <tr key={em.id} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)", background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.02)" }}>
                    <td style={{ padding: "10px 16px" }}>
                      <span style={{ background: "rgba(99,102,241,0.15)", color: "#a5b4fc", borderRadius: 6, padding: "3px 10px", fontSize: 12, fontWeight: 600 }}>
                        {em.trigger_key}
                      </span>
                    </td>
                    <td style={{ padding: "10px 16px", color: "#cbd5e1", fontSize: 13, lineHeight: 1.5 }}>{em.response}</td>
                    <td style={{ padding: "10px 16px", textAlign: "right" }}>
                      <button onClick={() => deleteEmotion(em.id)}
                        style={{ background: "rgba(239,68,68,0.12)", color: "#f87171", border: "none", borderRadius: 6, padding: "5px 10px", cursor: "pointer", fontSize: 11 }}>
                        DEL
                      </button>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={3} style={{ padding: "20px 16px", textAlign: "center", color: "#64748b", fontSize: 12 }}>No matches found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const renderRagCache = () => {
    const depts = [
      { key: 900001, name: "CEO", icon: "💼" },
      { key: 900002, name: "Marketing", icon: "📢" },
      { key: 900003, name: "Sale", icon: "💰" },
      { key: 900004, name: "Support", icon: "🛡�?" },
      { key: 900005, name: "Finance", icon: "💵" },
      { key: 900006, name: "Engineering", icon: "🛠�?" },
      { key: 900007, name: "Operation", icon: "⚙�?" },
      { key: 900008, name: "Legal", icon: "⚖�?" },
      { key: 900009, name: "Strategy", icon: "🎯" }
    ];

    const customBots = deployedAgents
      .filter(a => a.id < 900001 || a.id > 900009)
      .map(a => ({
        key: a.id,
        name: a.name,
        icon: a.archetype?.icon || "🤖"
      }));

    const allAgentsList = [...depts, ...customBots];

    const filteredCache = xaiCache.filter(entry => 
      entry.question.toLowerCase().includes(cacheSearch.toLowerCase()) ||
      entry.answer.toLowerCase().includes(cacheSearch.toLowerCase())
    );

    const clearAllAgentCache = async () => {
      if (!window.confirm("Are you sure you want to clear all cache entries for this agent?")) return;
      try {
        for (const entry of xaiCache) {
          await deleteRagCacheNoConfirm(entry.id);
        }
        loadXaiCache(cacheAgentKey);
      } catch {}
    };

    return (
      <div style={{ maxWidth: 1000 }}>
        {/* Header */}
        <div style={{ marginBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 10, letterSpacing: "0.12em", color: "#64748b", fontWeight: 700, marginBottom: 4 }}>
              🧠 RAG SEMANTIC CACHE WORKSPACE
            </div>
            <h1 style={{ fontSize: 24, fontWeight: 700, color: "#f8fafc", margin: 0 }}>Learned Cache Dashboard</h1>
          </div>
          {xaiCache.length > 0 && (
            <button
              onClick={clearAllAgentCache}
              style={{
                background: "rgba(239, 68, 68, 0.15)",
                border: "1px solid rgba(239, 68, 68, 0.3)",
                color: "#ef4444",
                padding: "8px 16px",
                borderRadius: 8,
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.2s"
              }}
            >
              🧹 Clear All Agent Cache
            </button>
          )}
        </div>

        {/* Dropdown Selector & Stats Grid */}
        <div style={{ display: "grid", gridTemplateColumns: "280px 1fr 1fr", gap: 16, marginBottom: 24 }}>
          {/* Agent Selector Card */}
          <div style={s.card}>
            <div style={{ ...s.sectionTitle, marginBottom: 8 }}>SELECT AGENT</div>
            <select
              value={cacheAgentKey}
              onChange={(e) => setCacheAgentKey(parseInt(e.target.value, 10))}
              style={{
                width: "100%",
                background: "#0d0f16",
                border: "1.5px solid rgba(255, 255, 255, 0.08)",
                borderRadius: 8,
                color: "#f8fafc",
                padding: "10px 12px",
                outline: "none",
                fontSize: 13,
                cursor: "pointer"
              }}
            >
              <optgroup label="Department Agents" style={{ background: "#0d0f16" }}>
                {depts.map(d => (
                  <option key={d.key} value={d.key} style={{ background: "#0d0f16" }}>
                    {d.icon} {d.name} Agent ({d.key})
                  </option>
                ))}
              </optgroup>
              {customBots.length > 0 && (
                <optgroup label="Custom Deployed Agents" style={{ background: "#0d0f16" }}>
                  {customBots.map(c => (
                    <option key={c.key} value={c.key} style={{ background: "#0d0f16" }}>
                      {c.icon} {c.name} ({c.key})
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>

          {/* Stats Card 1 */}
          <div style={s.card}>
            <div style={{ ...s.sectionTitle, marginBottom: 6 }}>TOTAL CACHED ENTRIES</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: "#6366f1", marginTop: 4 }}>
              {xaiCache.length}
            </div>
            <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>
              Learned from Grok / xAI API
            </div>
          </div>

          {/* Stats Card 2 */}
          <div style={s.card}>
            <div style={{ ...s.sectionTitle, marginBottom: 6 }}>ACTIVE STATUS</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: "#4ade80", marginTop: 4 }}>
              ONLINE
            </div>
            <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>
              Similarity threshold set to &gt;= 0.85
            </div>
          </div>
        </div>

        {/* Filter Input */}
        <div style={{ marginBottom: 20 }}>
          <input
            style={{
              ...s.input,
              maxWidth: 400,
              padding: "10px 14px",
              borderRadius: 8,
              fontSize: 13,
              margin: 0
            }}
            placeholder="�? Search cache by question or answer keywords..."
            value={cacheSearch}
            onChange={(e) => setCacheSearch(e.target.value)}
          />
        </div>

        {/* Cache List Table */}
        <div style={s.card}>
          <div style={s.sectionTitle}>
            🤖 LEARNED Q&A RECORDS FOR {allAgentsList.find(a => a.key === cacheAgentKey)?.icon} {allAgentsList.find(a => a.key === cacheAgentKey)?.name.toUpperCase()} ({filteredCache.length})
          </div>
          {filteredCache.length === 0 ? (
            <div style={{ textAlign: "center", color: "#475569", padding: "40px 0" }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>�?</div>
              <div style={{ fontSize: 13, fontWeight: 500 }}>No matching cache records found.</div>
              <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>
                Ask questions to this agent to trigger self-learning.
              </div>
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ color: "#475569", fontSize: 11, letterSpacing: "0.06em", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
                  <th style={{ textAlign: "left", padding: "10px 0", fontWeight: 600, width: "30%" }}>QUESTION</th>
                  <th style={{ textAlign: "left", padding: "10px 0", fontWeight: 600, width: "48%" }}>GROK / XAI LEARNED ANSWER</th>
                  <th style={{ textAlign: "right", padding: "10px 0", fontWeight: 600, width: "18%" }}>LEARNED AT</th>
                  <th style={{ width: 40 }}></th>
                </tr>
              </thead>
              <tbody>
                {filteredCache.map(entry => (
                  <tr key={entry.id} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                    <td style={{ padding: "12px 0", color: "#f1f5f9", verticalAlign: "top", fontWeight: 600, fontSize: 12 }}>
                      {entry.question}
                    </td>
                    <td style={{ padding: "12px 10px 12px 0", color: "#94a3b8", verticalAlign: "top", fontSize: 12, lineHeight: 1.5 }}>
                      {entry.answer}
                    </td>
                    <td style={{ padding: "12px 0", textAlign: "right", color: "#64748b", fontSize: 11, verticalAlign: "top" }}>
                      {formatDate(entry.created_at)}
                    </td>
                    <td style={{ padding: "12px 0", textAlign: "right", verticalAlign: "top" }}>
                      <button
                        onClick={() => deleteRagCache(entry.id)}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#ef4444",
                          cursor: "pointer",
                          fontSize: 14,
                          padding: "2px 8px"
                        }}
                      >
                        🗑
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  };

  const renderApiKeys = () => {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        {/* Top Header Card */}
        <div style={s.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                <span style={{ fontSize: 20 }}>⚡</span>
                <span style={{ fontSize: 16, fontWeight: 700, color: "#f8fafc" }}>Developer API Keys</span>
                <span style={{ fontSize: 11, background: "rgba(99,102,241,0.2)", color: "#a5b4fc", padding: "2px 8px", borderRadius: 12, fontWeight: 600 }}>v1 REST API</span>
              </div>
              <div style={{ color: "#94a3b8", fontSize: 12, maxWidth: 650, lineHeight: 1.5 }}>
                Generate and manage secret API keys for programmatic access to MuriAI. You can view, copy, activate, or revoke keys at any time.
              </div>
            </div>
          </div>

          {/* Create Key Form */}
          <div style={{ marginTop: 18, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: "#cbd5e1", marginBottom: 8 }}>CREATE NEW API KEY</div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <input
                type="text"
                placeholder="Key Name (e.g., Production Webhook, Mobile App, Backend Service)"
                value={apiKeyName}
                onChange={(e) => setApiKeyName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && createApiKey()}
                style={{
                  flex: 1, minWidth: 260, background: "rgba(0,0,0,0.3)",
                  border: "1px solid rgba(255,255,255,0.12)", color: "#f8fafc",
                  borderRadius: 6, padding: "8px 12px", fontSize: 13, outline: "none"
                }}
              />
              <button
                type="button"
                onClick={createApiKey}
                style={{
                  background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                  border: "none", color: "#fff", padding: "8px 18px",
                  borderRadius: 6, fontWeight: 600, fontSize: 12, cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 6, transition: "opacity 0.15s"
                }}
              >
                <span>+</span> Generate Key
              </button>
            </div>
            {apiKeyStatus && (
              <div style={{ marginTop: 10, fontSize: 12, color: apiKeyStatus.type === "success" ? "#34d399" : "#f87171" }}>
                {apiKeyStatus.message}
              </div>
            )}
          </div>
        </div>

        {/* API Keys Table */}
        <div style={s.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
            <div style={s.sectionTitle}>ACTIVE API KEYS ({apiKeys.length})</div>
            <button
              type="button"
              onClick={loadApiKeys}
              disabled={isRefreshingApiKeys}
              title="Refresh API Keys List"
              style={{
                background: isRefreshingApiKeys ? "rgba(99,102,241,0.25)" : "rgba(255,255,255,0.06)",
                border: "1px solid rgba(99,102,241,0.4)",
                color: isRefreshingApiKeys ? "#a5b4fc" : "#e2e8f0",
                padding: "5px 12px",
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 600,
                cursor: isRefreshingApiKeys ? "not-allowed" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                transition: "all 0.2s ease"
              }}
            >
              <span style={{
                display: "inline-block",
                transform: isRefreshingApiKeys ? "rotate(360deg)" : "rotate(0deg)",
                transition: isRefreshingApiKeys ? "transform 0.5s ease-in-out" : "none"
              }}>
                🔄
              </span>
              <span>{isRefreshingApiKeys ? "Refreshing..." : "Refresh"}</span>
            </button>
          </div>

          {apiKeys.length === 0 ? (
            <div style={{ color: "#64748b", textAlign: "center", padding: "30px 0", fontSize: 13 }}>
              No API keys generated yet. Create a key above to start building with the MuriAI API.
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ color: "#475569", borderBottom: "1px solid rgba(255,255,255,0.06)", fontSize: 11 }}>
                    <th style={{ textAlign: "left", padding: "8px 10px", fontWeight: 600 }}>NAME</th>
                    <th style={{ textAlign: "left", padding: "8px 10px", fontWeight: 600 }}>SECRET KEY</th>
                    <th style={{ textAlign: "center", padding: "8px 10px", fontWeight: 600 }}>STATUS</th>
                    <th style={{ textAlign: "left", padding: "8px 10px", fontWeight: 600 }}>CREATED</th>
                    <th style={{ textAlign: "right", padding: "8px 10px", fontWeight: 600 }}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {apiKeys.map((k) => {
                    const isRevealed = !!revealedKeys[k.id];
                    const isCopied = copiedKeyId === k.id;
                    const maskedKey = k.api_key.slice(0, 10) + "•".repeat(24) + k.api_key.slice(-4);
                    return (
                      <tr key={k.id} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                        <td style={{ padding: "12px 10px", fontWeight: 600, color: "#f1f5f9" }}>
                          {k.name}
                        </td>
                        <td style={{ padding: "12px 10px" }}>
                          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(0,0,0,0.3)", padding: "4px 8px", borderRadius: 6, border: "1px solid rgba(255,255,255,0.08)" }}>
                            <code style={{ fontFamily: "monospace", fontSize: 11, color: isRevealed ? "#a5b4fc" : "#94a3b8" }}>
                              {isRevealed ? k.api_key : maskedKey}
                            </code>
                            <button
                              type="button"
                              onClick={() => setRevealedKeys(prev => ({ ...prev, [k.id]: !prev[k.id] }))}
                              title={isRevealed ? "Hide Key" : "Reveal Key"}
                              style={{ background: "transparent", border: "none", color: "#64748b", cursor: "pointer", fontSize: 12, padding: "0 2px" }}
                            >
                              {isRevealed ? "🙈" : "👁️"}
                            </button>
                            <button
                              type="button"
                              onClick={() => copyToClipboard(k.api_key, k.id)}
                              title="Copy Key"
                              style={{
                                background: isCopied ? "rgba(52,211,153,0.2)" : "rgba(255,255,255,0.06)",
                                border: "1px solid " + (isCopied ? "#34d399" : "rgba(255,255,255,0.1)"),
                                color: isCopied ? "#34d399" : "#cbd5e1",
                                padding: "2px 6px", borderRadius: 4, cursor: "pointer", fontSize: 10, fontWeight: 600
                              }}
                            >
                              {isCopied ? "✓ Copied" : "Copy"}
                            </button>
                          </div>
                        </td>
                        <td style={{ padding: "12px 10px", textAlign: "center" }}>
                          <span
                            onClick={() => toggleApiKey(k.id)}
                            style={{
                              display: "inline-block",
                              padding: "2px 8px",
                              borderRadius: 12,
                              fontSize: 10,
                              fontWeight: 700,
                              cursor: "pointer",
                              background: k.is_active ? "rgba(16,185,129,0.15)" : "rgba(100,116,139,0.15)",
                              color: k.is_active ? "#34d399" : "#64748b",
                              border: `1px solid ${k.is_active ? "rgba(16,185,129,0.3)" : "rgba(100,116,139,0.3)"}`
                            }}
                            title="Click to toggle status"
                          >
                            {k.is_active ? "● ACTIVE" : "○ INACTIVE"}
                          </span>
                        </td>
                        <td style={{ padding: "12px 10px", color: "#64748b", fontSize: 11 }}>
                          {formatDate(k.created_at)}
                        </td>
                        <td style={{ padding: "12px 10px", textAlign: "right" }}>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedHookupKey(k.api_key);
                              if (hookupRef.current) hookupRef.current.scrollIntoView({ behavior: "smooth" });
                            }}
                            title="Configure external platform hookup with this key"
                            style={{
                              background: selectedHookupKey === k.api_key ? "rgba(99,102,241,0.25)" : "rgba(255,255,255,0.06)",
                              border: "1px solid " + (selectedHookupKey === k.api_key ? "#818cf8" : "rgba(255,255,255,0.12)"),
                              color: selectedHookupKey === k.api_key ? "#a5b4fc" : "#e2e8f0",
                              padding: "4px 9px",
                              borderRadius: 4,
                              cursor: "pointer",
                              fontSize: 11,
                              fontWeight: 600,
                              marginRight: 8
                            }}
                          >
                            🔌 Hookup
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteApiKey(k.id, k.name)}
                            title="Revoke & Delete Key"
                            style={{
                              background: "rgba(239,68,68,0.1)",
                              border: "1px solid rgba(239,68,68,0.25)",
                              color: "#f87171",
                              padding: "4px 8px",
                              borderRadius: 4,
                              cursor: "pointer",
                              fontSize: 11
                            }}
                          >
                            Revoke
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* External Platform Hookup Guide & Live Integration */}
        <div ref={hookupRef} style={{ ...s.card, border: "1px solid rgba(99,102,241,0.3)", background: "rgba(15,23,42,0.6)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                <span style={{ fontSize: 20 }}>🔌</span>
                <span style={{ fontSize: 16, fontWeight: 700, color: "#f8fafc" }}>External Platform Hookup & Code Integration</span>
                <span style={{ fontSize: 10, background: "rgba(52,211,153,0.15)", color: "#34d399", padding: "2px 8px", borderRadius: 12, fontWeight: 700, border: "1px solid rgba(52,211,153,0.3)" }}>
                  UNIVERSAL HOOKUP
                </span>
              </div>
              <div style={{ color: "#94a3b8", fontSize: 12, maxWidth: 700, lineHeight: 1.5 }}>
                Use this API key to connect MuriAI to <b>any other platform</b> (Hoppscotch, Postman, custom React/Next.js apps, Python scripts, Telegram/WhatsApp bots, Zapier/Make webhooks, or mobile apps).
              </div>
            </div>

            {/* Active Key Selector */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, background: "rgba(0,0,0,0.3)", padding: "6px 12px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.08)" }}>
              <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600 }}>Active Key:</span>
              {apiKeys.length === 0 ? (
                <span style={{ fontSize: 11, color: "#f87171" }}>No key available</span>
              ) : (
                <select
                  value={selectedHookupKey}
                  onChange={(e) => setSelectedHookupKey(e.target.value)}
                  style={{
                    background: "transparent",
                    color: "#a5b4fc",
                    border: "none",
                    fontSize: 12,
                    fontWeight: 600,
                    outline: "none",
                    cursor: "pointer"
                  }}
                >
                  {apiKeys.map(k => (
                    <option key={k.id} value={k.api_key} style={{ background: "#1e1e2e", color: "#f8fafc" }}>
                      {k.name} ({k.api_key.slice(0, 10)}...{k.api_key.slice(-4)})
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {/* Endpoint Details Bar */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10,
            background: "rgba(0,0,0,0.4)", padding: "10px 14px", borderRadius: 8, border: "1px solid rgba(255,255,255,0.06)", marginBottom: 18
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span style={{ fontSize: 11, fontWeight: 700, background: "#6366f1", color: "#fff", padding: "2px 8px", borderRadius: 4 }}>POST</span>
              <code style={{ fontFamily: "monospace", fontSize: 12, color: "#38bdf8", fontWeight: 600 }}>
                {`${API_BASE}/api/v1/chat`}
              </code>
            </div>
            <button
              type="button"
              onClick={() => copySnippet(`${API_BASE}/api/v1/chat`)}
              style={{
                background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)",
                color: "#e2e8f0", padding: "4px 10px", borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: "pointer"
              }}
            >
              📋 Copy Endpoint URL
            </button>
          </div>

          {/* Platform Tabs */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", borderBottom: "1px solid rgba(255,255,255,0.08)", paddingBottom: 10, marginBottom: 14 }}>
            {[
              { id: "curl", label: "cURL / Hoppscotch / Postman", icon: "🌐" },
              { id: "javascript", label: "JavaScript / React / Node.js", icon: "⚡" },
              { id: "python", label: "Python / AI Bot", icon: "🐍" },
              { id: "webhook", label: "Zapier / Make / Webhooks", icon: "🔗" },
              { id: "openai", label: "OpenAI SDK Compatible", icon: "🤖" },
            ].map(tab => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setHookupTab(tab.id)}
                style={{
                  background: hookupTab === tab.id ? "rgba(99,102,241,0.2)" : "rgba(255,255,255,0.03)",
                  border: `1px solid ${hookupTab === tab.id ? "#6366f1" : "rgba(255,255,255,0.06)"}`,
                  color: hookupTab === tab.id ? "#fff" : "#94a3b8",
                  padding: "6px 12px",
                  borderRadius: 6,
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  transition: "all 0.15s ease"
                }}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          {/* Code Snippet Box */}
          <div style={{ position: "relative", marginBottom: 20 }}>
            <div style={{
              position: "absolute", top: 10, right: 12, display: "flex", alignItems: "center", gap: 8, zIndex: 2
            }}>
              <button
                type="button"
                onClick={() => {
                  const snippets = {
                    curl: `curl -X POST "${API_BASE}/api/v1/chat" \\\n  -H "Authorization: Bearer ${selectedHookupKey || "muri_live_your_api_key_here"}" \\\n  -H "Content-Type: application/json" \\\n  -d '{\n    "message": "Hello, explain the responsibilities of the CEO.",\n    "agentKey": 900001\n  }'`,
                    javascript: `// Hookup to MuriAI from React / Next.js / Node.js\nasync function askMuriAI(message) {\n  const res = await fetch("${API_BASE}/api/v1/chat", {\n    method: "POST",\n    headers: {\n      "Authorization": "Bearer ${selectedHookupKey || "muri_live_your_api_key_here"}",\n      "Content-Type": "application/json"\n    },\n    body: JSON.stringify({\n      message: message,\n      agentKey: 900001\n    })\n  });\n  const data = await res.json();\n  return data.reply; // or data.choices[0].message.content\n}\n\n// Example call:\naskMuriAI("What is MuriAI?").then(console.log);`,
                    python: `# Hookup to MuriAI from Python / FastAPI / AI Agent\nimport requests\n\nurl = "${API_BASE}/api/v1/chat"\nheaders = {\n    "Authorization": "Bearer ${selectedHookupKey || "muri_live_your_api_key_here"}",\n    "Content-Type": "application/json"\n}\npayload = {\n    "message": "Hello from Python!",\n    "agentKey": 900001\n}\n\nresponse = requests.post(url, headers=headers, json=payload)\nprint("AI Response:", response.json().get("reply"))`,
                    webhook: `# Webhook Hookup (Zapier, Make, n8n, Retool, Chatbots)\nEndpoint URL: ${API_BASE}/api/v1/chat\nHTTP Method : POST\nHeaders     :\n  Authorization: Bearer ${selectedHookupKey || "muri_live_your_api_key_here"}\n  Content-Type: application/json\n  (Optional: x-api-key: ${selectedHookupKey || "muri_live_your_api_key_here"})\n\nJSON Body   :\n{\n  "message": "{{user_input_variable}}",\n  "agentKey": 900001\n}\n\nExtract Output : {{body.reply}}  or  {{body.choices[0].message.content}}`,
                    openai: `// OpenAI SDK Integration (Drop-in replacement for OpenAI API)\nimport OpenAI from "openai";\n\nconst client = new OpenAI({\n  apiKey: "${selectedHookupKey || "muri_live_your_api_key_here"}",\n  baseURL: "${API_BASE}/api/v1"\n});\n\nconst response = await client.chat.completions.create({\n  messages: [{ role: "user", content: "Tell me about our company" }]\n});\n\nconsole.log(response.choices[0].message.content);`
                  };
                  copySnippet(snippets[hookupTab]);
                }}
                style={{
                  background: copiedSnippet ? "rgba(52,211,153,0.2)" : "rgba(99,102,241,0.25)",
                  border: "1px solid " + (copiedSnippet ? "#34d399" : "rgba(99,102,241,0.5)"),
                  color: copiedSnippet ? "#34d399" : "#e0e7ff",
                  padding: "5px 12px",
                  borderRadius: 6,
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: "pointer"
                }}
              >
                {copiedSnippet ? "✓ Copied to Clipboard!" : "📋 Copy Code"}
              </button>
            </div>

            <pre style={{
              background: "#080b11",
              border: "1px solid rgba(255,255,255,0.08)",
              borderRadius: 8,
              padding: "16px 18px",
              fontFamily: "'Fira Code', 'Consolas', monospace",
              fontSize: 12,
              lineHeight: 1.6,
              color: "#e2e8f0",
              overflowX: "auto",
              margin: 0
            }}>
              {hookupTab === "curl" && (
                `curl -X POST "${API_BASE}/api/v1/chat" \\\n  -H "Authorization: Bearer ${selectedHookupKey || "muri_live_your_api_key_here"}" \\\n  -H "Content-Type: application/json" \\\n  -d '{\n    "message": "Hello, explain the responsibilities of the CEO.",\n    "agentKey": 900001\n  }'`
              )}
              {hookupTab === "javascript" && (
                `// Hookup to MuriAI from React / Next.js / Node.js\nasync function askMuriAI(message) {\n  const res = await fetch("${API_BASE}/api/v1/chat", {\n    method: "POST",\n    headers: {\n      "Authorization": "Bearer ${selectedHookupKey || "muri_live_your_api_key_here"}",\n      "Content-Type": "application/json"\n    },\n    body: JSON.stringify({\n      message: message,\n      agentKey: 900001\n    })\n  });\n  const data = await res.json();\n  return data.reply; // or data.choices[0].message.content\n}\n\n// Example call:\naskMuriAI("What is MuriAI?").then(console.log);`
              )}
              {hookupTab === "python" && (
                `# Hookup to MuriAI from Python / FastAPI / AI Agent\nimport requests\n\nurl = "${API_BASE}/api/v1/chat"\nheaders = {\n    "Authorization": "Bearer ${selectedHookupKey || "muri_live_your_api_key_here"}",\n    "Content-Type": "application/json"\n}\npayload = {\n    "message": "Hello from Python!",\n    "agentKey": 900001\n}\n\nresponse = requests.post(url, headers=headers, json=payload)\nprint("AI Response:", response.json().get("reply"))`
              )}
              {hookupTab === "webhook" && (
                `# Webhook Hookup (Zapier, Make, n8n, Retool, Chatbots)\nEndpoint URL: ${API_BASE}/api/v1/chat\nHTTP Method : POST\nHeaders     :\n  Authorization: Bearer ${selectedHookupKey || "muri_live_your_api_key_here"}\n  Content-Type: application/json\n  (Optional: x-api-key: ${selectedHookupKey || "muri_live_your_api_key_here"})\n\nJSON Body   :\n{\n  "message": "{{user_input_variable}}",\n  "agentKey": 900001\n}\n\nExtract Output : {{body.reply}}  or  {{body.choices[0].message.content}}`
              )}
              {hookupTab === "openai" && (
                `// OpenAI SDK Integration (Drop-in replacement for OpenAI API)\nimport OpenAI from "openai";\n\nconst client = new OpenAI({\n  apiKey: "${selectedHookupKey || "muri_live_your_api_key_here"}",\n  baseURL: "${API_BASE}/api/v1"\n});\n\nconst response = await client.chat.completions.create({\n  messages: [{ role: "user", content: "Tell me about our company" }]\n});\n\nconsole.log(response.choices[0].message.content);`
              )}
            </pre>
          </div>

          {/* Live Test Hookup Box */}
          <div style={{
            background: "rgba(255,255,255,0.02)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 10,
            padding: 16
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#cbd5e1", display: "flex", alignItems: "center", gap: 6 }}>
                <span>🧪</span>
                <span>TEST HOOKUP IN REAL-TIME</span>
                <span style={{ fontSize: 10, color: "#64748b", fontWeight: 400 }}>(Verifies your key directly against the /api/v1/chat endpoint)</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 11, color: "#94a3b8" }}>Target Agent:</span>
                <select
                  value={testHookupAgent}
                  onChange={(e) => setTestHookupAgent(e.target.value)}
                  style={{
                    background: "rgba(0,0,0,0.4)",
                    color: "#f8fafc",
                    border: "1px solid rgba(255,255,255,0.12)",
                    borderRadius: 6,
                    padding: "4px 8px",
                    fontSize: 11,
                    outline: "none"
                  }}
                >
                  <option value="900001">900001 - CEO Agent</option>
                  <option value="900002">900002 - Marketing Agent</option>
                  <option value="900003">900003 - Sale Agent</option>
                  <option value="900004">900004 - Support Agent</option>
                  <option value="900005">900005 - Finance Agent</option>
                  <option value="900006">900006 - Engineering Agent</option>
                  <option value="900007">900007 - Operation Agent</option>
                  <option value="900008">900008 - Legal Agent</option>
                  <option value="900009">900009 - Strategy Agent</option>
                </select>
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
              <input
                type="text"
                value={testHookupMsg}
                onChange={(e) => setTestHookupMsg(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && runHookupTest()}
                placeholder="Type a test query (e.g. Tell me about MuriAI / Vanakkam)..."
                style={{
                  flex: 1, minWidth: 260, background: "rgba(0,0,0,0.3)",
                  border: "1px solid rgba(255,255,255,0.12)", color: "#f8fafc",
                  borderRadius: 6, padding: "8px 12px", fontSize: 12, outline: "none"
                }}
              />
              <button
                type="button"
                onClick={runHookupTest}
                disabled={testHookupLoading || !selectedHookupKey}
                style={{
                  background: testHookupLoading ? "rgba(99,102,241,0.3)" : "linear-gradient(135deg, #10b981, #059669)",
                  border: "none", color: "#fff", padding: "8px 16px",
                  borderRadius: 6, fontWeight: 600, fontSize: 12, cursor: testHookupLoading ? "not-allowed" : "pointer",
                  display: "flex", alignItems: "center", gap: 6, transition: "opacity 0.15s"
                }}
              >
                <span>{testHookupLoading ? "⏳" : "🚀"}</span>
                <span>{testHookupLoading ? "Sending Request..." : "Test Hookup"}</span>
              </button>
            </div>

            {testHookupResponse && (
              <div style={{
                marginTop: 12,
                background: testHookupResponse.ok ? "rgba(16,185,129,0.08)" : "rgba(239,68,68,0.08)",
                border: "1px solid " + (testHookupResponse.ok ? "rgba(16,185,129,0.3)" : "rgba(239,68,68,0.3)"),
                borderRadius: 8,
                padding: 12
              }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 4,
                      background: testHookupResponse.ok ? "#10b981" : "#ef4444", color: "#fff"
                    }}>
                      {testHookupResponse.status ? `${testHookupResponse.status} ${testHookupResponse.ok ? "OK" : "ERROR"}` : "FAILED"}
                    </span>
                    <span style={{ fontSize: 11, color: "#94a3b8" }}>
                      Latency: <strong style={{ color: "#f8fafc" }}>{testHookupResponse.latency}ms</strong>
                    </span>
                    {testHookupResponse.data?.route && (
                      <span style={{ fontSize: 11, color: "#94a3b8" }}>
                        Route: <strong style={{ color: "#a5b4fc" }}>{testHookupResponse.data.route.toUpperCase()}</strong>
                      </span>
                    )}
                  </div>
                  <span style={{ fontSize: 11, color: "#64748b" }}>
                    Key: {testHookupResponse.data?.keyName || "verified"}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: testHookupResponse.ok ? "#e2e8f0" : "#fca5a5", lineHeight: 1.5, maxHeight: 160, overflowY: "auto", whiteSpace: "pre-wrap" }}>
                  {testHookupResponse.ok
                    ? (testHookupResponse.data?.reply || JSON.stringify(testHookupResponse.data, null, 2))
                    : (testHookupResponse.error || testHookupResponse.data?.error || "Request failed")}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (activeSection === "coupons") return renderCoupons();
    if (activeSection === "plans") return renderPlans();
    if (activeSection === "emotions") return renderEmotions();
    if (activeSection === "rag-cache") return renderRagCache();
    if (activeSection === "api") return renderApiKeys();

    if (activeSection === "ai-agents") {
      const customAgents = deployedAgents.map(a => ({
        key: a.id,
        name: a.name,
        icon: a.archetype?.icon || "🤖",
        desc: a.archetype?.desc || "Agent"
      }));

      const depts = customAgents.length > 0 ? customAgents : [
        { key: 900001, name: "CEO", icon: "💼", desc: "CEO Agent" },
        { key: 900002, name: "Marketing", icon: "📢", desc: "Marketing Agent" },
        { key: 900003, name: "Sale", icon: "💰", desc: "Sale Agent" },
        { key: 900004, name: "Support", icon: "🛡️", desc: "Support Agent" },
        { key: 900005, name: "Finance", icon: "💵", desc: "Finance Agent" },
        { key: 900006, name: "Engineering", icon: "🛠️", desc: "Engineering Agent" },
        { key: 900007, name: "Operation", icon: "⚙️", desc: "Operation Agent" },
        { key: 900008, name: "Legal", icon: "⚖️", desc: "Legal Agent" },
        { key: 900009, name: "Strategy", icon: "🎯", desc: "Strategy Agent" }
      ];

      const selectedAgent = deployedAgents.find(a => Number(a.id) === Number(selectedAgentKey));
      const currentAgentPdfs = selectedAgent ? (selectedAgent.pdfFiles || []) : [];

      return (
        <div>
          {/* Tab Switcher */}
          <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
            <button
              onClick={() => setAgentTab("departments")}
              style={{
                background: agentTab === "departments" ? "#6366f1" : "rgba(255,255,255,0.04)",
                border: `1px solid ${agentTab === "departments" ? "#6366f1" : "rgba(255,255,255,0.08)"}`,
                color: agentTab === "departments" ? "#fff" : "#94a3b8",
                fontWeight: 600,
                borderRadius: 8,
                padding: "8px 16px",
                cursor: "pointer",
                outline: "none",
                transition: "all 0.2s"
              }}
            >
              💼 Department Workspace
            </button>
            <button
              onClick={() => setAgentTab("custom")}
              style={{
                background: agentTab === "custom" ? "#6366f1" : "rgba(255,255,255,0.04)",
                border: `1px solid ${agentTab === "custom" ? "#6366f1" : "rgba(255,255,255,0.08)"}`,
                color: agentTab === "custom" ? "#fff" : "#94a3b8",
                fontWeight: 600,
                borderRadius: 8,
                padding: "8px 16px",
                cursor: "pointer",
                outline: "none",
                transition: "all 0.2s"
              }}
            >
              🛠�? Custom Agent Creator
            </button>
          </div>

          {agentTab === "departments" ? (
            <div>
              {/* Agent Selection Grid */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ ...s.sectionTitle, marginBottom: 10 }}>SELECT AGENT WORKSPACE</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
                  {depts.map(d => {
                    const agent = deployedAgents.find(a => a.id === d.key);
                    const isAct = agent ? agent.isActive : false;
                    return (
                      <AgentCard
                        key={d.key}
                        icon={d.icon}
                        title={d.name.toUpperCase()}
                        desc={isAct ? "🟢 Active" : "🔴 Inactive"}
                        selected={selectedAgentKey === d.key}
                        onClick={() => setSelectedAgentKey(d.key)}
                      />
                    );
                  })}
                  <div
                    onClick={() => setAgentTab("custom")}
                    style={{
                      flex: "1 1 160px",
                      background: "rgba(255,255,255,0.02)",
                      border: "1.5px dashed rgba(255,255,255,0.2)",
                      borderRadius: 12, padding: "18px 14px", cursor: "pointer", transition: "all 0.2s", textAlign: "center",
                      display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center"
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(99,102,241,0.08)"; e.currentTarget.style.borderColor = "#6366f1"; e.currentTarget.style.color = "#a5b4fc"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.02)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.2)"; e.currentTarget.style.color = "#94a3b8"; }}
                  >
                    <div style={{ fontSize: 28, marginBottom: 6 }}>➕</div>
                    <div style={{ fontWeight: 600, fontSize: 12, letterSpacing: "0.08em" }}>CREATE NEW AGENT</div>
                  </div>
                </div>
              </div>

              {/* Agent Workspace Header */}
              <div style={{ ...s.card, background: "rgba(99,102,241,0.06)", border: "1px solid rgba(99,102,241,0.2)", padding: "14px 20px", marginBottom: 20, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ fontSize: 24 }}>{depts.find(d => d.key === selectedAgentKey)?.icon || "🤖"}</span>
                  <div>
                    <span style={{ fontWeight: 700, letterSpacing: "0.06em", fontSize: 14 }}>
                      {depts.find(d => d.key === selectedAgentKey)?.name.toUpperCase()} WORKSPACE
                    </span>
                    <div style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
                      Agent Key: {selectedAgentKey} · Scoped Library Context Configuration
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  {selectedAgent ? (
                    <>
                      {selectedAgentKey > 900009 && (
                        <button
                          onClick={() => {
                            deleteAgent(selectedAgent);
                            setSelectedAgentKey(900001);
                          }}
                          style={{
                            background: "rgba(239,68,68,0.15)",
                            border: "1px solid rgba(239,68,68,0.3)",
                            borderRadius: 6,
                            color: "#f87171",
                            padding: "5px 12px",
                            fontSize: 11,
                            cursor: "pointer",
                            fontWeight: 600,
                            transition: "all 0.2s"
                          }}
                        >
                          🗑 Delete Agent
                        </button>
                      )}
                      <div style={{
                        background: selectedAgent.isActive ? "rgba(34,197,94,0.15)" : "rgba(239,68,68,0.15)",
                        border: `1px solid ${selectedAgent.isActive ? "rgba(34,197,94,0.3)" : "rgba(239,68,68,0.3)"}`,
                        color: selectedAgent.isActive ? "#4ade80" : "#f87171",
                        borderRadius: 6, padding: "4px 10px", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em"
                      }}>
                        {selectedAgent.isActive ? "ACTIVE" : "INACTIVE"}
                      </div>
                      <button
                        onClick={toggleAgentActive}
                        style={{
                          background: "rgba(255,255,255,0.05)",
                          border: "1px solid rgba(255,255,255,0.1)",
                          borderRadius: 6,
                          color: "#cbd5e1",
                          padding: "5px 12px",
                          fontSize: 11,
                          cursor: "pointer",
                          fontWeight: 600,
                          transition: "all 0.2s"
                        }}
                      >
                        {selectedAgent.isActive ? "Deactivate" : "Activate"}
                      </button>
                    </>
                  ) : (
                    <div style={{ background: "rgba(251,191,36,0.15)", border: "1px solid rgba(251,191,36,0.3)", color: "#fbbf24", borderRadius: 6, padding: "4px 10px", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em" }}>
                      NOT PROVISIONED
                    </div>
                  )}
                </div>
              </div>

              <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
                {/* Left Column - Library Upload and Files List */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={s.card}>
                    <div style={s.sectionTitle}><span style={{ color: "#6366f1" }}>01</span> 📂 DOCUMENT UPLOAD</div>
                    <div onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={handleDeptFileDrop}
                      onClick={() => fileInputRef.current?.click()}
                      style={{ border: `2px dashed ${dragOver ? "#6366f1" : "rgba(255,255,255,0.1)"}`, borderRadius: 12, padding: "40px 20px", textAlign: "center", cursor: "pointer", background: dragOver ? "rgba(99,102,241,0.08)" : "rgba(255,255,255,0.02)", transition: "all 0.2s" }}>
                      <input ref={fileInputRef} type="file" accept=".pdf,.docx,.doc,.xlsx,.xls,.csv,.pptx,.txt,.md,.json,.html,.htm,.png,.jpg,.jpeg" multiple style={{ display: "none" }} onChange={handleDeptFileSelect} />
                      {uploading ? (<div><div style={{ fontSize: 28, marginBottom: 8 }}>⏳</div><div style={{ color: "#94a3b8" }}>Uploading & indexing document…</div></div>)
                        : (<><div style={{ fontSize: 32, marginBottom: 8 }}>📁</div><div style={{ fontWeight: 600, marginBottom: 4 }}>Drop any document to add to this agent's library</div><div style={{ color: "#64748b", fontSize: 12 }}>PDF · DOCX · XLSX · CSV · PPTX · TXT · MD · JSON · HTML · PNG · JPG</div><div style={{ color: "#475569", fontSize: 11, marginTop: 4 }}>Click or drag & drop · Multiple files supported</div></>)}
                    </div>
                    {uploadStatus && <div style={s.statusBox(uploadStatus.type)}>{uploadStatus.message}</div>}
                  </div>

                  {/* Scoped Library Table */}
                  <div style={s.card}>
                    <div style={s.sectionTitle}>📚 SCOPED AGENT LIBRARY — {currentAgentPdfs.length} FILE{currentAgentPdfs.length !== 1 ? "S" : ""}</div>
                    {currentAgentPdfs.length === 0 ? (
                      <div style={{ textAlign: "center", color: "#475569", padding: "20px 0" }}>
                        <div style={{ fontSize: 28, marginBottom: 8 }}>📤</div>
                        <div style={{ fontSize: 12 }}>Library is currently empty. Upload any document or ask questions to build knowledge.</div>
                      </div>
                    ) : (
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead><tr style={{ color: "#475569", fontSize: 11, letterSpacing: "0.06em" }}>
                          <th style={{ textAlign: "left", padding: "6px 0", fontWeight: 600 }}>FILENAME</th>
                          <th style={{ textAlign: "center", padding: "6px 0", fontWeight: 600, width: 56 }}>TYPE</th>
                          <th style={{ textAlign: "right", padding: "6px 0", fontWeight: 600 }}>SIZE</th>
                          <th style={{ textAlign: "right", padding: "6px 0", fontWeight: 600 }}>UPLOADED</th>
                          <th style={{ width: 120 }}></th>
                        </tr></thead>
                        <tbody>
                          {currentAgentPdfs.map(filename => {
                            const fileMeta = libraryFiles.find(lf => lf.filename === filename);
                            const displayName = filename.replace(/^\d+_/, "");
                            const ext = filename.split(".").pop().toLowerCase();
                            const fileIcons = { pdf: "📄", md: "📝", docx: "📄", doc: "📄", xlsx: "📈", xls: "📈", csv: "📊", pptx: "📊", txt: "📃", json: "{ }", html: "🌐", htm: "🌐", png: "🖼", jpg: "🖼", jpeg: "🖼" };
                            const icon = fileIcons[ext] || "📁";
                            const typeColors = { pdf: "#f87171", md: "#a3e635", docx: "#60a5fa", doc: "#60a5fa", xlsx: "#34d399", xls: "#34d399", csv: "#34d399", pptx: "#fb923c", txt: "#94a3b8", json: "#fbbf24", html: "#c084fc", htm: "#c084fc", png: "#38bdf8", jpg: "#38bdf8", jpeg: "#38bdf8" };
                            const typeColor = typeColors[ext] || "#64748b";
                            return (
                              <tr key={filename} style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                                <td style={{ padding: "9px 0", color: "#e2e8f0" }}>{icon} {displayName}</td>
                                <td style={{ padding: "9px 0", textAlign: "center" }}>
                                  <span style={{ background: typeColor + "22", color: typeColor, border: `1px solid ${typeColor}44`, borderRadius: 4, padding: "1px 5px", fontSize: 9, fontWeight: 700, letterSpacing: "0.06em" }}>{ext.toUpperCase()}</span>
                                </td>
                                <td style={{ padding: "9px 0", textAlign: "right", color: "#64748b" }}>{fileMeta ? formatBytes(fileMeta.size) : "—"}</td>
                                <td style={{ padding: "9px 0", textAlign: "right", color: "#64748b", fontSize: 11 }}>{fileMeta ? formatDate(fileMeta.uploadedAt) : "—"}</td>
                                <td style={{ padding: "9px 0", textAlign: "right", whiteSpace: "nowrap" }}>
                                   <button onClick={() => viewFile(filename, false)} title="View" style={{ background: "none", border: "none", color: "#818cf8", cursor: "pointer", fontSize: 14, marginRight: 6 }}>👁</button>
                                   <button onClick={() => viewFile(filename, true)} title="Edit" style={{ background: "none", border: "none", color: "#eab308", cursor: "pointer", fontSize: 14, marginRight: 6 }}>✏️</button>
                                   <button onClick={() => handleDownloadFile(filename)} title="Download" style={{ background: "none", border: "none", color: "#38bdf8", cursor: "pointer", fontSize: 14, marginRight: 6 }}>📥</button>
                                   <button onClick={() => handleDeptFileDelete(filename)} title="Delete" style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontSize: 14 }}>🗑</button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>

                {/* Right Column - Gaps and Q&A Review */}
                <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 0 }}>
                  {/* Unanswered / Gap Questions */}
                  <div style={s.card}>
                    <div style={s.sectionTitle}>⚠️ UNANSWERED / GAP QUESTIONS ({ragMisses.length})</div>
                    {ragMisses.length === 0 ? (
                      <div style={{ textAlign: "center", color: "#475569", padding: "20px 0" }}>
                        <div style={{ fontSize: 28, marginBottom: 8 }}>🎉</div>
                        <div style={{ fontSize: 12 }}>No unresolved gaps! All questions have been matched or successfully answered.</div>
                      </div>
                    ) : (
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead><tr style={{ color: "#475569", fontSize: 11, letterSpacing: "0.06em" }}>
                          <th style={{ textAlign: "left", padding: "6px 0", fontWeight: 600 }}>QUESTION</th>
                          <th style={{ textAlign: "right", padding: "6px 0", fontWeight: 600, width: 140 }}>ASKED AT</th>
                          <th style={{ width: 100 }}></th>
                        </tr></thead>
                        <tbody>
                          {ragMisses.map(miss => (
                            <tr key={miss.id} style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                              <td style={{ padding: "9px 0", color: "#cbd5e1" }}>{miss.question}</td>
                              <td style={{ padding: "9px 0", textAlign: "right", color: "#64748b", fontSize: 11 }}>{formatDate(miss.asked_at)}</td>
                              <td style={{ padding: "9px 0", textAlign: "right" }}>
                                <button
                                  onClick={() => resolveRagMiss(miss.id)}
                                  style={{ background: "rgba(34,197,94,0.15)", color: "#4ade80", border: "none", borderRadius: 6, padding: "4px 8px", cursor: "pointer", fontSize: 11, fontWeight: 600 }}
                                >
                                  Resolve
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {/* Grok / xAI Cache Review */}
                  <div style={s.card}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                      <div style={s.sectionTitle}>🤖 GROK / XAI Q&A REVIEW (LEARNED CACHE) ({xaiCache.length})</div>
                      {xaiCache.length > 0 && (
                        <button
                          id="generate-knowledge-pdf-btn"
                          onClick={handleGenerateKnowledgePdf}
                          disabled={generatingKnowledge}
                          title="Compile all learned Q&A into a searchable knowledge document"
                          style={{
                            background: generatingKnowledge ? "rgba(99,102,241,0.15)" : "linear-gradient(135deg, #6366f1, #8b5cf6)",
                            border: "none", borderRadius: 8, color: "#fff", padding: "7px 14px",
                            fontSize: 11, fontWeight: 700, cursor: generatingKnowledge ? "not-allowed" : "pointer",
                            letterSpacing: "0.05em", transition: "all 0.2s", opacity: generatingKnowledge ? 0.6 : 1
                          }}
                        >
                          {generatingKnowledge ? "⏳ Generating…" : "⚡ Generate Knowledge Doc"}
                        </button>
                      )}
                    </div>
                    {knowledgeGenStatus && (
                      <div style={s.statusBox(knowledgeGenStatus.type)}>{knowledgeGenStatus.message}</div>
                    )}
                    {xaiCache.length === 0 ? (
                      <div style={{ textAlign: "center", color: "#475569", padding: "20px 0" }}>
                        <div style={{ fontSize: 28, marginBottom: 8 }}>🔍</div>
                        <div style={{ fontSize: 12 }}>No learned cache entries for this agent yet.</div>
                      </div>
                    ) : (
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead><tr style={{ color: "#475569", fontSize: 11, letterSpacing: "0.06em" }}>
                          <th style={{ textAlign: "left", padding: "6px 0", fontWeight: 600, width: "30%" }}>QUESTION</th>
                          <th style={{ textAlign: "left", padding: "6px 0", fontWeight: 600, width: "45%" }}>ANSWER</th>
                          <th style={{ textAlign: "right", padding: "6px 0", fontWeight: 600, width: "20%" }}>LEARNED AT</th>
                          <th style={{ width: 40 }}></th>
                        </tr></thead>
                        <tbody>
                          {xaiCache.map(entry => (
                            <tr key={entry.id} style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                              <td style={{ padding: "9px 0", color: "#e2e8f0", verticalAlign: "top" }}>{entry.question}</td>
                              <td style={{ padding: "9px 0", color: "#94a3b8", verticalAlign: "top", fontSize: 12, lineHeight: 1.4 }}>{entry.answer}</td>
                              <td style={{ padding: "9px 0", textAlign: "right", color: "#64748b", fontSize: 11, verticalAlign: "top" }}>{formatDate(entry.created_at)}</td>
                              <td style={{ padding: "9px 0", textAlign: "right", verticalAlign: "top" }}>
                                <button
                                  onClick={() => deleteRagCache(entry.id)}
                                  style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", fontSize: 14 }}
                                >
                                  🗑
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ ...s.card, background: "rgba(99,102,241,0.06)", border: "1px solid rgba(99,102,241,0.2)", padding: "14px 20px", marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: 16 }}>🤖</span>
                    <span style={{ fontWeight: 700, letterSpacing: "0.06em", fontSize: 13 }}>CUSTOM AGENT CREATION WORKSPACE</span>
                  </div>
                  <div style={{ background: "#22c55e22", border: "1px solid #22c55e44", color: "#4ade80", borderRadius: 6, padding: "3px 10px", fontSize: 10, fontWeight: 700, letterSpacing: "0.1em" }}>READY TO DEPLOY</div>
                </div>
                <div style={s.card}>
                  <div style={s.sectionTitle}><span style={{ color: "#6366f1" }}>01</span> — AGENT ARCHETYPE</div>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    {archetypes.map(a => <AgentCard key={a.id} {...a} selected={selectedArchetype === a.id} onClick={() => setSelectedArchetype(a.id)} />)}
                  </div>
                  <div style={{ marginTop: 14 }}>
                    <input style={s.input} placeholder="Agent name (e.g. 'HR Support Bot')" value={agentName} onChange={e => setAgentName(e.target.value)} />
                  </div>
                  {selectedArchetype === "custom" && (
                    <div style={{ marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap" }}>
                      <input style={{ ...s.input, flex: "0 0 60px", textAlign: "center", margin: 0 }} placeholder="Icon" value={customArchetypeIcon} onChange={e => setCustomArchetypeIcon(e.target.value)} title="Agent Icon (emoji)" />
                      <input style={{ ...s.input, flex: "1 1 200px", margin: 0 }} placeholder="Archetype Title (e.g. 'TECHNICAL LEAD')" value={customArchetypeTitle} onChange={e => setCustomArchetypeTitle(e.target.value)} />
                      <input style={{ ...s.input, flex: "1 1 250px", margin: 0 }} placeholder="Archetype Description (e.g. 'Code review and architecture')" value={customArchetypeDesc} onChange={e => setCustomArchetypeDesc(e.target.value)} />
                    </div>
                  )}
                </div>
                <div style={s.card}>
                  <div style={s.sectionTitle}><span style={{ color: "#6366f1" }}>02</span> — RAG DATA PORTAL</div>
                  <div onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    style={{ border: `2px dashed ${dragOver ? "#6366f1" : "rgba(255,255,255,0.1)"}`, borderRadius: 12, padding: "32px 20px", textAlign: "center", cursor: uploading ? "default" : "pointer", background: dragOver ? "rgba(99,102,241,0.08)" : "rgba(255,255,255,0.02)", transition: "all 0.2s" }}>
                    <input ref={fileInputRef} type="file" accept=".pdf" multiple style={{ display: "none" }} onChange={handleFileSelect} />
                    {uploading ? (<div><div style={{ fontSize: 28, marginBottom: 8, animation: "spin 1s linear infinite" }}>⏳</div><div style={{ color: "#94a3b8", fontSize: 13 }}>Uploading & indexing PDF…</div></div>)
                      : (<><div style={{ fontSize: 30, marginBottom: 6 }}>📎</div><div style={{ fontWeight: 600, marginBottom: 3, fontSize: 14 }}>Drop PDF for this agent</div><div style={{ color: "#64748b", fontSize: 12 }}>Click or drag & drop — multiple PDFs supported</div></>)}
                  </div>

                  {/* Uploaded PDFs list */}
                  {pendingPdfFiles.length > 0 && (
                    <div style={{ marginTop: 14 }}>
                      <div style={{ fontSize: 10, letterSpacing: "0.1em", color: "#64748b", fontWeight: 700, marginBottom: 8 }}>📚 UPLOADED PDFs — {pendingPdfFiles.length} FILE{pendingPdfFiles.length !== 1 ? "S" : ""}</div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {pendingPdfFiles.map((fname, idx) => {
                          const displayName = fname.replace(/^\d+_/, "");
                          const fileMeta = libraryFiles.find(lf => lf.filename === fname);
                          return (
                            <div key={idx} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "rgba(99,102,241,0.07)", border: "1px solid rgba(99,102,241,0.2)", borderRadius: 8, padding: "8px 12px" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span style={{ fontSize: 16 }}>📄</span>
                                <div>
                                  <div style={{ color: "#e2e8f0", fontSize: 12, fontWeight: 600 }}>{displayName}</div>
                                  <div style={{ color: "#64748b", fontSize: 11 }}>{fileMeta ? (fileMeta.size / 1024).toFixed(1) + " KB" : "Processing..."}</div>
                                </div>
                              </div>
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span style={{ background: "rgba(34,197,94,0.15)", color: "#4ade80", borderRadius: 4, padding: "2px 8px", fontSize: 10, fontWeight: 700 }}>✓ READY</span>
                                <button
                                  onClick={() => setPendingPdfFiles(prev => prev.filter((_, i) => i !== idx))}
                                  style={{ background: "rgba(239,68,68,0.12)", color: "#f87171", border: "none", borderRadius: 6, padding: "4px 9px", cursor: "pointer", fontSize: 12, fontWeight: 700 }}
                                  title="Remove from deploy list"
                                >✕</button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {uploadStatus && <div style={{ ...s.statusBox(uploadStatus.type), marginTop: 10 }}>{uploadStatus.message}</div>}
                </div>
                <button style={{ ...s.btn, width: "100%", justifyContent: "center", padding: "14px", fontSize: 14, borderRadius: 10 }} onClick={deployAgent}>
                  ⚡ DEPLOY / SAVE AGENT
                </button>
              </div>

              {/* Active fleet for deployed agents */}
              <div style={{ width: 280, flexShrink: 0 }}>
                <div style={{ ...s.card, minHeight: 200 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                    <div style={{ fontWeight: 700, fontSize: 13 }}>DEPLOYED AGENTS FLEET</div>
                    <div style={{ background: deployedAgents.length > 0 ? "rgba(99,102,241,0.2)" : "rgba(255,255,255,0.06)", color: deployedAgents.length > 0 ? "#a5b4fc" : "#64748b", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700 }}>{deployedAgents.length} AGENTS</div>
                  </div>
                  {deployedAgents.length === 0 ? (
                    <div style={{ textAlign: "center", color: "#475569", padding: "30px 0" }}><div style={{ fontSize: 32, marginBottom: 8 }}>🤖</div><div style={{ fontSize: 12 }}>No agents deployed yet.</div></div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {deployedAgents.map(agent => (
                        <div key={agent.id} style={{ background: agent.isActive ? "rgba(34,197,94,0.08)" : "rgba(99,102,241,0.08)", border: `1px solid ${agent.isActive ? "rgba(34,197,94,0.3)" : "rgba(99,102,241,0.2)"}`, borderRadius: 10, padding: "12px", position: "relative" }}>
                          {agent.isActive && <div style={{ position: "absolute", top: 8, left: 10, background: "rgba(34,197,94,0.2)", border: "1px solid #22c55e44", color: "#4ade80", borderRadius: 4, padding: "1px 7px", fontSize: 9, fontWeight: 700 }}>�? ACTIVE</div>}
                          <button onClick={() => deleteAgent(agent)} style={{ position: "absolute", top: 8, right: 8, background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.25)", borderRadius: 6, color: "#f87171", cursor: "pointer", fontSize: 11, padding: "2px 7px", fontWeight: 600 }}>✕</button>
                          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 2, paddingTop: agent.isActive ? 18 : 0, paddingRight: 36 }}>{agent.archetype?.icon} {agent.name}</div>
                          <div style={{ fontSize: 11, color: "#64748b" }}>{agent.archetype?.desc}</div>
                          <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 4 }}>{agent.pdfs} PDF{agent.pdfs !== 1 ? "s" : ""} · {agent.deployedAt}</div>
                          {/* Add PDFs to existing agent */}
                          <label style={{ display: "block", marginTop: 8, cursor: "pointer" }}>
                            <input type="file" accept=".pdf" multiple style={{ display: "none" }}
                              onChange={(e) => { uploadPdfsToAgent([...e.target.files], agent.id); e.target.value = ""; }} />
                            <span style={{ display: "block", textAlign: "center", background: "rgba(255,255,255,0.05)", border: "1px dashed rgba(255,255,255,0.15)", borderRadius: 6, color: "#94a3b8", fontSize: 11, padding: "5px 0", fontWeight: 600 }}>
                              📎 Add PDFs
                            </span>
                          </label>
                          {!agent.isActive ? (
                            <button onClick={() => setActiveAgent(agent.id)} style={{ marginTop: 6, width: "100%", background: "rgba(99,102,241,0.2)", border: "1px solid rgba(99,102,241,0.4)", borderRadius: 6, color: "#a5b4fc", cursor: "pointer", fontSize: 11, padding: "5px 0", fontWeight: 600 }}>⚡ Set Active</button>
                          ) : (
                            <button onClick={() => deactivateAgent(agent.id)} style={{ marginTop: 6, width: "100%", background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.3)", borderRadius: 6, color: "#f87171", cursor: "pointer", fontSize: 11, padding: "5px 0", fontWeight: 600 }}>�?� Deactivate</button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

    if (activeSection === "users") return (
      <div style={s.card}>
        <div style={s.sectionTitle}>👤 REGISTERED USERS — {users.length}</div>
        {users.length === 0 ? <div style={{ color: "#475569" }}>No users found.</div> : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr style={{ color: "#475569", fontSize: 11 }}>
              <th style={{ textAlign: "left", padding: "6px 0", fontWeight: 600 }}>USERNAME</th>
              <th style={{ textAlign: "right", padding: "6px 0", fontWeight: 600 }}>JOINED</th>
            </tr></thead>
            <tbody>
              {users.map((u, i) => (
                <tr key={i} style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
                  <td style={{ padding: "9px 0" }}>{u.username}</td>
                  <td style={{ padding: "9px 0", textAlign: "right", color: "#64748b", fontSize: 11 }}>{formatDate(u.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    );

    if (activeSection === "analytics") return (
      <div style={s.card}>
        <div style={s.sectionTitle}>?? ACTIVITY LOG (last 50)</div>
        {activity.slice(0, 50).map((a, i) => (
          <div key={i} style={{ borderTop: "1px solid rgba(255,255,255,0.05)", padding: "8px 0", display: "flex", gap: 12, alignItems: "flex-start" }}>
            <span style={{ fontSize: 11, color: "#475569", whiteSpace: "nowrap", minWidth: 130 }}>{formatDate(a.created_at)}</span>
            <span style={{ fontWeight: 600, fontSize: 11, color: "#a5b4fc", minWidth: 80 }}>{a.action}</span>
            <span style={{ fontSize: 12, color: "#94a3b8" }}>{a.username}</span>
            <span style={{ fontSize: 11, color: "#64748b", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.detail}</span>
          </div>
        ))}
      </div>
    );

    return (
      <div style={{ ...s.card, color: "#475569", textAlign: "center", padding: 60 }}>
        <div style={{ fontSize: 40, marginBottom: 10 }}>??</div>
        <div style={{ fontWeight: 600 }}>This section is coming soon.</div>
      </div>
    );
  };

  return (
    <div style={s.root}>
      <aside style={s.sidebar}>
        <div style={s.brand}><div style={s.brandDot} /> AdminAI</div>
        {navItems.map(item => (
          <React.Fragment key={item.id}>
            {item.group && <div style={s.groupLabel}>{item.group}</div>}
            <div style={s.navItem(activeSection === item.id)} onClick={() => setActiveSection(item.id)}>
              <span style={{ fontSize: 14 }}>{item.icon}</span><span>{item.label}</span>
            </div>
          </React.Fragment>
        ))}
        <div style={{ marginTop: "auto", padding: "12px 16px" }}>
          <div style={{ ...s.navItem(false), color: "#ef4444", cursor: "pointer" }} onClick={onLogout}>
            <span>🚪</span> Logout
          </div>
        </div>
      </aside>
      <main style={s.main}>
        <header style={s.topbar}>
          <div style={{ fontWeight: 700, fontSize: 15, letterSpacing: "0.05em" }}>
            {navItems.find(n => n.id === activeSection)?.icon}&nbsp;
            {navItems.find(n => n.id === activeSection)?.label?.toUpperCase()}
          </div>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <div style={{ fontSize: 11, color: "#475569" }}>{deployedAgents.length} agent{deployedAgents.length !== 1 ? "s" : ""} deployed</div>
            <div style={{ width: 30, height: 30, borderRadius: "50%", background: "#6366f1", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 13 }}>A</div>
          </div>
        </header>
        <div style={s.content}>{renderContent()}</div>
      </main>

      {/* File View/Edit Modal */}
      {viewFileModal && (
        <div onClick={() => setViewFileModal(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: "#0f172a", border: "1px solid rgba(99,102,241,0.3)", borderRadius: 12, width: "90%", maxWidth: 850, maxHeight: "85vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            {/* Modal Header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 20px", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span>{viewFileModal.type === "md" ? "📝" : "📄"}</span>
                <span style={{ fontWeight: 600, color: "#e2e8f0", fontSize: 14 }}>{viewFileModal.filename}</span>
                <span style={{ fontSize: 11, color: "#475569", background: "rgba(99,102,241,0.15)", padding: "2px 8px", borderRadius: 4 }}>{viewFileModal.type?.toUpperCase()}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <button
                  onClick={() => handleDownloadFile(viewFileModal.rawFilename)}
                  title="Download File"
                  style={{ background: "rgba(56,189,248,0.15)", color: "#38bdf8", border: "1px solid rgba(56,189,248,0.3)", borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}
                >
                  📥 Download
                </button>
                {!viewFileModal.isEditing ? (
                  <button
                    onClick={() => setViewFileModal(prev => ({ ...prev, isEditing: true }))}
                    title="Edit Content"
                    style={{ background: "rgba(234,179,8,0.15)", color: "#eab308", border: "1px solid rgba(234,179,8,0.3)", borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}
                  >
                    ✏️ Edit
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => setViewFileModal(prev => ({ ...prev, isEditing: false }))}
                      style={{ background: "rgba(255,255,255,0.1)", color: "#94a3b8", border: "none", borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: 12 }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveFileContent}
                      disabled={viewFileModal.saving}
                      style={{ background: "#6366f1", color: "#fff", border: "none", borderRadius: 6, padding: "4px 12px", cursor: "pointer", fontSize: 12, fontWeight: 600 }}
                    >
                      {viewFileModal.saving ? "Saving..." : "💾 Save Changes"}
                    </button>
                  </>
                )}
                <button onClick={() => setViewFileModal(null)} style={{ background: "none", border: "none", color: "#64748b", cursor: "pointer", fontSize: 20, lineHeight: 1, marginLeft: 6 }}>✕</button>
              </div>
            </div>
            {/* Modal Content */}
            <div style={{ flex: 1, padding: "16px 20px", display: "flex", flexDirection: "column", overflow: "hidden", minHeight: 350 }}>
              {viewFileModal.isEditing ? (
                <textarea
                  value={viewFileModal.content}
                  onChange={e => setViewFileModal(prev => ({ ...prev, content: e.target.value }))}
                  style={{
                    width: "100%", height: "100%", flex: 1, background: "rgba(0,0,0,0.3)", border: "1px solid rgba(99,102,241,0.3)",
                    borderRadius: 8, padding: 12, color: "#f8fafc", fontSize: 13, fontFamily: "'JetBrains Mono', monospace",
                    lineHeight: 1.6, resize: "none", outline: "none"
                  }}
                />
              ) : (
                <pre style={{ margin: 0, padding: 0, overflowY: "auto", flex: 1, fontSize: 12.5, lineHeight: 1.7, color: "#cbd5e1", fontFamily: "'JetBrains Mono', 'Fira Code', monospace", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                  {viewFileModal.content}
                </pre>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
